import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import DesktopFeed from './DesktopFeed';
import DesktopProfileView from './DesktopProfileView';
import DesktopGroupView from './DesktopGroupView';
import DesktopPeopleBrowse from './DesktopPeopleBrowse';
import DesktopSearchModal from './DesktopSearchModal';
import DesktopCreateModal from './DesktopCreateModal';
import DesktopAddPersonModal from './DesktopAddPersonModal';
import DesktopAddToGroupModal from './DesktopAddToGroupModal';
import DesktopImportModal from './DesktopImportModal';
import DesktopSettings from './DesktopSettings';
import DesktopStrategyView from './DesktopStrategyView';
import { useChatLogic } from '../../hooks/useChatLogic';
import { authFetch, authPost } from '../../lib/authFetch';
import { createGroup, supabaseGroupToAppFormat } from '../../lib/supabaseDb';
import { supabase } from '../../lib/supabase';
import { isLocalModeEnabled, getLocalDonors, addLocalDonor, updateLocalDonor, addLocalGroup, addLocalDonorToGroup, addLocalList, deleteLocalList, addLocalTable, deleteLocalTable, getLocalTables } from '../../lib/localMode';
import WordReveal from '../shared/WordReveal';
import './DesktopApp.css';

function DesktopApp({ donors, groups, savedLists = [], savedTables = [], onRefreshDonors, user, subscription, onSignOut }) {
  // Current view: null = feed, otherwise shows that view
  const [currentView, setCurrentView] = useState(null); // null | 'people' | 'profile' | 'group' | 'groups' | 'list' | 'table' | 'settings' | 'strategy'
  const [viewData, setViewData] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchPosition, setSearchPosition] = useState(null);
  const searchButtonRef = useRef(null);
  const [scrollToEntryId, setScrollToEntryId] = useState(null);

  // Chat modal state (for non-feed views)
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [chatInputFocused, setChatInputFocused] = useState(false);
  const [pageChatEntries, setPageChatEntries] = useState([]);
  const [, setPageChatStreaming] = useState(null);
  const [pageChatAnimatingId, setPageChatAnimatingId] = useState(null);
  const [chatbarLoading, setChatbarLoading] = useState(false);
  const [highlightedPeople, setHighlightedPeople] = useState([]);
  const chatInputRef = useRef(null);
  const pageChatHistoryRef = useRef(null);

  // Edit context modal state
  const [editContextModal, setEditContextModal] = useState({ open: false, group: null, position: null });
  const [editContextValue, setEditContextValue] = useState('');
  const [isResearchingContext, setIsResearchingContext] = useState(false);
  const editContextInputRef = useRef(null);

  // Create group modal state
  const [createGroupModal, setCreateGroupModal] = useState({ open: false });
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupContext, setNewGroupContext] = useState('');
  const [isResearchingNewGroup, setIsResearchingNewGroup] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const newGroupNameRef = useRef(null);

  // Add person modal state
  const [addPersonModalOpen, setAddPersonModalOpen] = useState(false);
  const [addPersonToGroup, setAddPersonToGroup] = useState(null); // group to add person to
  const [addPersonPosition, setAddPersonPosition] = useState(null); // position for positioned modal
  const [addPersonPrefill, setAddPersonPrefill] = useState({ name: '', note: '' }); // prefill from chat
  const [isFirstPersonFlow, setIsFirstPersonFlow] = useState(false); // onboarding: first person being added

  // Sidebar hint for visiting first group (onboarding)
  const [showGroupsHint, setShowGroupsHint] = useState(false);

  // Onboarding: prompt to add person to group after they're created
  const [onboardingGroupPrompt, setOnboardingGroupPrompt] = useState({ open: false, personName: null });

  // Onboarding: show hint on chat bar after first person is added
  const [showFirstPersonHint, setShowFirstPersonHint] = useState(false);

  // Onboarding: sidebar tour (1 = chat, 2 = groups, 0 = done)
  const [sidebarTourStep, setSidebarTourStep] = useState(0);
  const chatButtonRef = useRef(null);
  const groupsButtonRef = useRef(null);
  const [tourTooltipPos, setTourTooltipPos] = useState({ top: 0, left: 0 });
  const [readyForSidebarTour, setReadyForSidebarTour] = useState(false); // triggers when leaving profile

  // Update tooltip position when tour step changes
  useEffect(() => {
    if (sidebarTourStep === 1 && chatButtonRef.current) {
      const rect = chatButtonRef.current.getBoundingClientRect();
      setTourTooltipPos({
        top: rect.top + rect.height / 2,
        left: rect.right + 20
      });
    } else if (sidebarTourStep === 2 && groupsButtonRef.current) {
      const rect = groupsButtonRef.current.getBoundingClientRect();
      setTourTooltipPos({
        top: rect.top + rect.height / 2,
        left: rect.right + 20
      });
    }
  }, [sidebarTourStep]);

  // Set People view as default for new users (no people yet)
  useEffect(() => {
    const noPeople = !donors || Object.keys(donors).length === 0;
    if (noPeople && currentView === null) {
      setCurrentView('people');
    }
  }, [donors, currentView]);

  // Add choice modal (Add Person vs Import)
  const [addChoiceModalOpen, setAddChoiceModalOpen] = useState(false);
  const [addChoicePosition, setAddChoicePosition] = useState(null);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Delete confirmation modal state (supports groups, lists, tables)
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ open: false, type: null, item: null, position: null });

  // Add to group modal state
  const [addToGroupModal, setAddToGroupModal] = useState({ open: false, group: null });

  // Create list/table modal state
  const [createModal, setCreateModal] = useState({ open: false, type: null, group: null });

  // List/Table search state
  const [listTableSearch, setListTableSearch] = useState('');

  // Group menu modal state
  const [groupMenuModal, setGroupMenuModal] = useState({ open: false, group: null, position: null });

  // Upcoming modal state (tasks + meetings)
  const [upcomingModalOpen, setUpcomingModalOpen] = useState(false);

  // Undo system state
  const [undoItems, setUndoItems] = useState([]);

  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpModalPosition, setHelpModalPosition] = useState(null);
  const [helpEmail, setHelpEmail] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpSubmitting, setHelpSubmitting] = useState(false);
  const [helpSuccess, setHelpSuccess] = useState(false);
  const [helpError, setHelpError] = useState('');
  const helpButtonRef = useRef(null);

  // Table editing state
  const [editingCell, setEditingCell] = useState(null); // { rowIdx, colIdx, value }
  const [upcomingTab, setUpcomingTab] = useState('meetings'); // 'meetings' | 'tasks'
  const [upcomingSearch, setUpcomingSearch] = useState('');
  // eslint-disable-next-line no-unused-vars
  const upcomingButtonRef = useRef(null);
  const [upcomingPosition, setUpcomingPosition] = useState(null);

  // Local state for groups/lists/tables
  const [localGroups, setLocalGroups] = useState(groups || []);
  const [localLists, setLocalLists] = useState(savedLists || []);
  const [localTables, setLocalTables] = useState(savedTables || []);

  // User context state (for personalization)
  const [userContext, setUserContext] = useState(null);
  const [onboardingComplete, setOnboardingComplete] = useState(true); // Assume true until we check

  // Format relative date for upcoming modal
  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';

    // If it's a natural language date without a year, just display it as-is
    const hasYear = /\b20\d{2}\b/.test(dateStr) || /^\d{4}-\d{2}-\d{2}/.test(dateStr);
    if (!hasYear) return dateStr;

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    // Sanity check: if the parsed year is more than 2 years off, return as-is
    const now = new Date();
    const yearDiff = now.getFullYear() - date.getFullYear();
    if (yearDiff > 2 || yearDiff < -2) return dateStr;

    const diffMs = now - date;
    const diffDays = Math.floor(Math.abs(diffMs) / 86400000);
    const isFuture = diffMs < 0;

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return isFuture ? 'Tomorrow' : 'Yesterday';
    if (diffDays < 7) return isFuture ? `In ${diffDays}d` : `${diffDays}d ago`;
    if (diffDays < 30) return isFuture ? `In ${Math.floor(diffDays / 7)}w` : `${Math.floor(diffDays / 7)}w ago`;
    return isFuture ? `In ${Math.floor(diffDays / 30)}mo` : `${Math.floor(diffDays / 30)}mo ago`;
  };

  // Extract tasks and meetings from donor narratives
  const extractedTasks = useMemo(() => {
    const tasks = [];

    Object.entries(donors || {}).forEach(([name, data]) => {
      // Use the actual tasks array from donor data (properly extracted by AI)
      const donorTasks = data.tasks || [];
      donorTasks.forEach(task => {
        if (task && (task.text || typeof task === 'string')) {
          tasks.push({
            person: name,
            task: task.text || task,
            due: task.due || null,
            source: 'tasks'
          });
        }
      });

      // Also check nextTask field as fallback
      if (data.nextTask && data.nextTask !== 'No tasks' && data.nextTask !== 'Unknown') {
        // Don't add if we already have tasks for this person
        const hasTasksForPerson = tasks.some(t => t.person === name);
        if (!hasTasksForPerson) {
          tasks.push({
            person: name,
            task: data.nextTask,
            due: data.nextMeeting || null,
            source: 'nextTask'
          });
        }
      }
    });

    return tasks.slice(0, 50);
  }, [donors]);

  const extractedMeetings = useMemo(() => {
    const meetings = [];

    Object.entries(donors || {}).forEach(([name, data]) => {
      // Use nextMeeting field if it has a real scheduled meeting
      if (data.nextMeeting && data.nextMeeting !== 'Not scheduled' && data.nextMeeting !== 'Unknown') {
        meetings.push({
          person: name,
          meeting: `Meeting scheduled`,
          date: data.nextMeeting,
          source: 'nextMeeting'
        });
      }

      // Also check timeline for future meeting events
      const timeline = data.timelineEvents || data.timeline_events || data.timeline || [];
      timeline.forEach(entry => {
        if ((entry.type === 'meeting' || entry.type === 'call' || entry.type === 'event') && !entry.isPast) {
          meetings.push({
            person: name,
            meeting: entry.event || entry.content || entry.description || 'Meeting',
            date: entry.date,
            source: 'timeline'
          });
        }
      });
    });

    return meetings.slice(0, 50);
  }, [donors]);

  // Help modal handlers
  const handleHelpSubmit = async (e) => {
    e.preventDefault();
    if (!helpMessage.trim() || helpSubmitting) return;

    setHelpSubmitting(true);
    setHelpError('');

    try {
      if (supabase) {
        const { error } = await supabase
          .from('help_inquiries')
          .insert([
            {
              email: helpEmail.trim() || user?.email || null,
              message: helpMessage.trim(),
              source: 'desktop_app',
              created_at: new Date().toISOString()
            }
          ]);

        if (error) {
          console.error('Supabase error:', error);
          setHelpError('Failed to send. Please try again.');
        } else {
          setHelpSuccess(true);
          setHelpMessage('');
          setHelpEmail('');
        }
      }
    } catch (err) {
      console.error('Error submitting help request:', err);
      setHelpError('Failed to send. Please try again.');
    } finally {
      setHelpSubmitting(false);
    }
  };

  const closeHelpModal = () => {
    setShowHelpModal(false);
    setHelpModalPosition(null);
    setHelpError('');
    setHelpSuccess(false);
  };

  // Navigation
  const navigateTo = (view, data = null) => {
    // Check if leaving profile with sidebar tour ready
    if (currentView === 'profile' && view !== 'profile' && readyForSidebarTour) {
      setReadyForSidebarTour(false);
      // Start sidebar tour when navigating away from profile
      setTimeout(() => {
        setSidebarTourStep(1);
      }, 300);
    }

    // Close chat modal and clear page chat when navigating
    setChatModalOpen(false);
    setPageChatEntries([]);
    setPageChatStreaming(null);
    setHighlightedPeople([]);
    setListTableSearch(''); // Clear search when navigating

    if (view === 'feed' || view === null) {
      setCurrentView(null);
      setViewData(null);
      setNavigationHistory([]);
      return;
    }

    // Save history for back navigation
    if (currentView !== view || JSON.stringify(viewData) !== JSON.stringify(data)) {
      setNavigationHistory(prev => [...prev, { view: currentView, data: viewData }]);
    }
    setCurrentView(view);
    setViewData(data);
  };

  const goBack = () => {
    // Check if leaving profile with sidebar tour ready
    if (currentView === 'profile' && readyForSidebarTour) {
      setReadyForSidebarTour(false);
      setTimeout(() => {
        setSidebarTourStep(1);
      }, 300);
    }

    setChatModalOpen(false);
    setPageChatEntries([]);
    setPageChatStreaming(null);

    // Lists and tables should go back to their parent group, showing the appropriate tab
    if (currentView === 'list' || currentView === 'table') {
      const parentGroupId = viewData?.groupId || viewData?.group_id;
      const parentGroup = localGroups.find(g => g.id === parentGroupId);
      if (parentGroup) {
        setNavigationHistory([]);
        setCurrentView('group');
        // Pass initialTab to show lists or tables tab
        setViewData({ ...parentGroup, initialTab: currentView === 'list' ? 'lists' : 'tables' });
        return;
      }
    }

    if (navigationHistory.length > 0) {
      const prev = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory(h => h.slice(0, -1));
      setCurrentView(prev.view);
      setViewData(prev.data);
    } else {
      // Go back to feed
      setCurrentView(null);
      setViewData(null);
    }
  };

  const handleNavigateToGroup = (group) => navigateTo('group', group);
  const handleNavigateToPerson = (personName) => navigateTo('profile', { name: personName });
  const handleNavigateToList = (listPreview) => {
    // Navigate to the actual list view, not the group
    // Map listId to id for consistency with saved lists
    navigateTo('list', {
      ...listPreview,
      id: listPreview.listId || listPreview.id,
      name: listPreview.listName || listPreview.name
    });
  };

  const handleShowCreateGroupSheet = (forPerson, suggestedName) => {
    const groupName = prompt(suggestedName ? `Create group "${suggestedName}"?` : 'Enter a name for your new group:');
    if (groupName?.trim()) {
      // Will be handled by the hook's handleCreateGroupFromFeed
    }
  };

  // Handler to open add person modal with prefilled data (called from chat when user mentions someone new)
  const handleOpenAddPersonModal = (name, options = {}) => {
    const { isFirstPerson = false, note = '' } = typeof options === 'string' ? { note: options } : options;
    console.log('[Onboarding] handleOpenAddPersonModal called with isFirstPerson:', isFirstPerson);
    setAddPersonPrefill({ name: name || '', note: note || '' });
    setAddPersonToGroup(null);
    setIsFirstPersonFlow(isFirstPerson);
    // Position modal to appear from the top of the chat bar
    const feedInputPosition = {
      bottom: 100, // Position just above the chat input bar
      left: 300 // Align with feed content area (after sidebar)
    };
    setAddPersonPosition(feedInputPosition);
    setAddPersonModalOpen(true);
  };

  // === UNDO SYSTEM ===
  const addUndoItem = (item) => {
    const undoId = Date.now();
    const undoEntry = { id: undoId, ...item };
    setUndoItems(prev => [...prev, undoEntry]);

    // Auto-remove and execute permanent delete after 5 seconds
    setTimeout(() => {
      setUndoItems(prev => {
        const stillExists = prev.find(u => u.id === undoId);
        if (stillExists && stillExists.onExpire) {
          stillExists.onExpire();
        }
        return prev.filter(u => u.id !== undoId);
      });
    }, 5000);

    return undoId;
  };

  const handleUndo = (undoId) => {
    const undoEntry = undoItems.find(u => u.id === undoId);
    if (undoEntry?.onUndo) {
      undoEntry.onUndo();
    }
    setUndoItems(prev => prev.filter(u => u.id !== undoId));
  };

  // === TABLE EDITING ===
  const handleTableCellClick = (rowIdx, colIdx, value, isNameCol) => {
    // Don't edit name column (it's a link)
    if (isNameCol) return;
    setEditingCell({ rowIdx, colIdx, value: value === '—' ? '' : value });
  };

  const handleTableCellSave = async () => {
    if (!editingCell || !viewData?.id) return;

    const { rowIdx, colIdx, value } = editingCell;
    const newRows = [...(viewData.rows || [])];

    // Update the cell value
    if (Array.isArray(newRows[rowIdx])) {
      newRows[rowIdx] = [...newRows[rowIdx]];
      newRows[rowIdx][colIdx] = value || '—';
    }

    // Update local state immediately
    setViewData(prev => ({ ...prev, rows: newRows }));
    setEditingCell(null);

    // Save to backend
    try {
      await authFetch(`/api/tables/${viewData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: newRows })
      });
    } catch (error) {
      console.error('Error saving table cell:', error);
    }
  };

  const handleTableRowDelete = (rowIdx) => {
    if (!viewData?.rows) return;

    const deletedRow = viewData.rows[rowIdx];
    const newRows = viewData.rows.filter((_, idx) => idx !== rowIdx);

    // Update local state immediately
    setViewData(prev => ({ ...prev, rows: newRows }));

    // Add to undo with restore capability
    addUndoItem({
      type: 'table-row',
      label: 'Row deleted',
      onUndo: () => {
        // Restore the row at its original position
        setViewData(prev => {
          const restoredRows = [...(prev.rows || [])];
          restoredRows.splice(rowIdx, 0, deletedRow);
          return { ...prev, rows: restoredRows };
        });
      },
      onExpire: async () => {
        // Permanently delete from backend
        try {
          await authFetch(`/api/tables/${viewData.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rows: newRows })
          });
        } catch (error) {
          console.error('Error deleting table row:', error);
        }
      }
    });
  };

  // === LIST EDITING ===
  const handleListPersonRemove = (groupIdx, personIdx, personName) => {
    if (!viewData?.groups) return;

    const removedPerson = personName;
    const newGroups = viewData.groups.map((group, gi) => {
      if (gi === groupIdx) {
        return {
          ...group,
          people: group.people.filter((_, pi) => pi !== personIdx)
        };
      }
      return group;
    });

    // Update local state immediately
    setViewData(prev => ({ ...prev, groups: newGroups }));

    // Add to undo
    addUndoItem({
      type: 'list-person',
      label: `${personName} removed`,
      onUndo: () => {
        // Restore the person
        setViewData(prev => {
          const restoredGroups = prev.groups.map((group, gi) => {
            if (gi === groupIdx) {
              const restoredPeople = [...group.people];
              restoredPeople.splice(personIdx, 0, removedPerson);
              return { ...group, people: restoredPeople };
            }
            return group;
          });
          return { ...prev, groups: restoredGroups };
        });
      },
      onExpire: async () => {
        // Permanently update backend
        try {
          await authFetch(`/api/lists/${viewData.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ groups: newGroups })
          });
        } catch (error) {
          console.error('Error removing person from list:', error);
        }
      }
    });
  };

  // Load/refresh user context (defined before useChatLogic so it can be passed as callback)
  const refreshUserContext = async () => {
    try {
      const response = await authFetch('/api/user-context');
      if (response.ok) {
        const data = await response.json();
        setUserContext(data.userContext);
        setOnboardingComplete(data.onboardingComplete || false);
        console.log('[DesktopApp] User context loaded:', data.onboardingComplete ? 'onboarding complete' : 'show welcome');
      }
    } catch (error) {
      console.error('Failed to load user context:', error);
    }
  };

  // Use the shared chat logic hook
  const {
    feedEntries,
    setFeedEntries,
    feedLoading,
    isSubmitting,
    streamingText,
    streamingEntryId,
    narrativeUpdate,
    setNarrativeUpdate,
    handleSubmit: originalHandleSubmit,
    handleGroupSelect,
    handleSkipGroupSelect,
    handleCreateGroupFromFeed,
    handleViewList,
    handleCreateListForGroup,
    handleConfirmAdd,
    handleDeclineAdd,
    handleResearchPerson,
    handleSkipResearch,
    handleSkipMoreInfo,
    saveFeedEntryToDb,
    refreshGroupListsAndTables
  } = useChatLogic({
    donors,
    groups: localGroups,
    savedLists: localLists,
    savedTables: localTables,
    onRefreshDonors,
    onNavigateToGroup: handleNavigateToGroup,
    onNavigateToPerson: handleNavigateToPerson,
    onNavigateToList: handleNavigateToList,
    onShowCreateGroupSheet: handleShowCreateGroupSheet,
    onOpenAddPersonModal: handleOpenAddPersonModal,
    setLocalGroups,
    setLocalLists,
    setLocalTables,
    userContext, // Pass cached user context to avoid DB fetch on every chat
    onRefreshUserContext: refreshUserContext // Refresh after onboarding
  });

  // Get context data for the current page
  const getPageContext = () => {
    switch (currentView) {
      case 'profile':
        const donor = donors?.[viewData?.name];
        return {
          type: 'profile',
          name: viewData?.name,
          data: donor ? { ...donor, name: viewData?.name } : null
        };
      case 'group':
        const groupMembers = Object.entries(donors || {})
          .filter(([_, d]) => d.groupId === viewData?.id || d.group_id === viewData?.id)
          .map(([name, data]) => ({ name, ...data }));
        return {
          type: 'group',
          name: viewData?.name,
          context: viewData?.context,
          members: groupMembers
        };
      case 'groups':
        return { type: 'groups', groups: localGroups };
      case 'people':
        return { type: 'people', count: Object.keys(donors || {}).length };
      case 'list':
        return {
          type: 'list',
          name: viewData?.organizationPrompt || viewData?.name || 'List',
          groups: viewData?.groups || []
        };
      case 'table':
        return {
          type: 'table',
          name: viewData?.name || 'Table',
          rows: viewData?.rows?.length || 0
        };
      default:
        return { type: currentView };
    }
  };

  // Extract mentioned people names from a message
  const extractMentionedPeople = (message) => {
    if (!message) return [];
    const donorNames = Object.keys(donors || {});
    const mentioned = [];

    donorNames.forEach(fullName => {
      const pattern = new RegExp('\\b' + fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i');
      if (pattern.test(message)) {
        mentioned.push(fullName);
      }
    });

    return mentioned;
  };

  // Render pagechat message with highlighting
  const renderPageChatMessage = (message) => {
    if (!message) return null;

    // Get all donor names for matching
    const donorNames = Object.keys(donors || {});

    // Build patterns for names, amounts, and dates
    const patterns = [];
    const nameMap = {};

    donorNames.forEach(fullName => {
      patterns.push(fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      nameMap[fullName.toLowerCase()] = fullName;
      const firstName = fullName.split(' ')[0];
      if (firstName.length > 2) {
        patterns.push(firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        nameMap[firstName.toLowerCase()] = fullName;
      }
    });

    // Sort by length (longest first) to match full names before first names
    patterns.sort((a, b) => b.length - a.length);

    // Amount pattern
    const amountPattern = '\\$[\\d,]+[kKmM]?|\\b\\d{1,3}(?:,\\d{3})+(?:[kKmM])?\\b|\\b\\d+[kKmM]\\b';

    // Build combined regex
    const allPatterns = [amountPattern];
    if (patterns.length > 0) {
      allPatterns.push('\\b(?:' + patterns.join('|') + ')\\b');
    }
    const combinedPattern = new RegExp('(' + allPatterns.join('|') + ')', 'gi');

    const parts = message.split(combinedPattern);
    const isAmount = (str) => /^(\$[\d,]+[kKmM]?|\d{1,3}(?:,\d{3})+[kKmM]?|\d+[kKmM])$/i.test(str);

    return parts.map((part, i) => {
      if (!part) return null;
      if (isAmount(part)) {
        return <span key={i} className="pagechat-amount">{part}</span>;
      }
      const matchedName = nameMap[part.toLowerCase()];
      if (matchedName) {
        return (
          <span
            key={i}
            className="pagechat-name"
            onClick={() => navigateTo('profile', { name: matchedName })}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Handle pagechat submit (separate from main feed)
  const handlePageChatSubmit = async (message) => {
    setChatModalOpen(true);

    // Add user message to page chat
    const userEntry = {
      id: `pc-${Date.now()}`,
      type: 'question',
      message,
      timestamp: new Date().toISOString()
    };
    setPageChatEntries(prev => [...prev, userEntry]);

    // Get page context
    const pageContext = getPageContext();

    // Special handling for list modifications
    if (currentView === 'list' && viewData?.id) {
      const aiEntryId = `pc-ai-${Date.now()}`;
      setPageChatStreaming(aiEntryId);
      setPageChatEntries(prev => [...prev, {
        id: aiEntryId,
        type: 'answer',
        message: '',
        timestamp: new Date().toISOString()
      }]);

      try {
        const response = await authFetch(`/api/lists/${viewData.id}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message,
            currentList: viewData
          })
        });

        if (response.ok) {
          const data = await response.json();
          const finalMessage = data.response || 'Done.';

          setPageChatEntries(prev => prev.map(e =>
            e.id === aiEntryId ? { ...e, message: finalMessage } : e
          ));

          // Trigger word reveal animation
          setPageChatAnimatingId(aiEntryId);
          const wordCount = finalMessage.split(/\s+/).filter(w => w.trim()).length;
          setTimeout(() => setPageChatAnimatingId(null), wordCount * 50 + 500);

          // If the list was updated, refresh the view
          if (data.updatedList) {
            setViewData(data.updatedList);
            // Also update in localLists
            setLocalLists(prev => prev.map(l =>
              l.id === data.updatedList.id ? data.updatedList : l
            ));
          }

          // Add to feed
          const now = Date.now();
          const feedQuestionEntry = {
            id: crypto.randomUUID ? crypto.randomUUID() : `q-${now}`,
            type: 'question',
            message: message,
            timestamp: new Date(now).toISOString()
          };
          const feedAnswerEntry = {
            id: crypto.randomUUID ? crypto.randomUUID() : `a-${now + 1}`,
            type: 'answer',
            message: finalMessage,
            status: 'received',
            timestamp: new Date(now + 1).toISOString()
          };
          setFeedEntries(prev => [feedAnswerEntry, feedQuestionEntry, ...prev]);
          saveFeedEntryToDb(feedQuestionEntry);
          saveFeedEntryToDb(feedAnswerEntry);
        } else {
          setPageChatEntries(prev => prev.map(e =>
            e.id === aiEntryId ? { ...e, message: 'Sorry, something went wrong.' } : e
          ));
        }
      } catch (error) {
        console.error('List chat error:', error);
        setPageChatEntries(prev => prev.map(e =>
          e.id === aiEntryId ? { ...e, message: 'Sorry, something went wrong.' } : e
        ));
      } finally {
        setPageChatStreaming(null);
      }
      return;
    }

    // Special handling for table modifications
    if (currentView === 'table' && viewData?.id) {
      const aiEntryId = `pc-ai-${Date.now()}`;
      setPageChatStreaming(aiEntryId);
      setPageChatEntries(prev => [...prev, {
        id: aiEntryId,
        type: 'answer',
        message: '',
        timestamp: new Date().toISOString()
      }]);

      try {
        const response = await authFetch(`/api/tables/${viewData.id}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message,
            currentTable: viewData
          })
        });

        if (response.ok) {
          const data = await response.json();
          const finalMessage = data.response || 'Done.';

          setPageChatEntries(prev => prev.map(e =>
            e.id === aiEntryId ? { ...e, message: finalMessage } : e
          ));

          // Trigger word reveal animation
          setPageChatAnimatingId(aiEntryId);
          const wordCount = finalMessage.split(/\s+/).filter(w => w.trim()).length;
          setTimeout(() => setPageChatAnimatingId(null), wordCount * 50 + 500);

          // If the table was updated, refresh the view
          if (data.updatedTable) {
            setViewData(data.updatedTable);
            // Also update in localTables
            setLocalTables(prev => prev.map(t =>
              t.id === data.updatedTable.id ? data.updatedTable : t
            ));
          }

          // Add to feed
          const now = Date.now();
          const feedQuestionEntry = {
            id: crypto.randomUUID ? crypto.randomUUID() : `q-${now}`,
            type: 'question',
            message: message,
            timestamp: new Date(now).toISOString()
          };
          const feedAnswerEntry = {
            id: crypto.randomUUID ? crypto.randomUUID() : `a-${now + 1}`,
            type: 'answer',
            message: finalMessage,
            status: 'received',
            timestamp: new Date(now + 1).toISOString()
          };
          setFeedEntries(prev => [feedAnswerEntry, feedQuestionEntry, ...prev]);
          saveFeedEntryToDb(feedQuestionEntry);
          saveFeedEntryToDb(feedAnswerEntry);
        } else {
          setPageChatEntries(prev => prev.map(e =>
            e.id === aiEntryId ? { ...e, message: 'Sorry, something went wrong.' } : e
          ));
        }
      } catch (error) {
        console.error('Table chat error:', error);
        setPageChatEntries(prev => prev.map(e =>
          e.id === aiEntryId ? { ...e, message: 'Sorry, something went wrong.' } : e
        ));
      } finally {
        setPageChatStreaming(null);
      }
      return;
    }

    // Local Storage Mode: same bundle used by the main feed's handler, since
    // this "page chat" path is a second, separate route to add/update a person
    // (used on every view except the home feed). List/table chat above this
    // block is untouched - not part of Local Mode's scope.
    if (isLocalModeEnabled()) {
      const aiEntryId = `pc-ai-${Date.now()}`;
      setPageChatStreaming(aiEntryId);
      setPageChatEntries(prev => [...prev, { id: aiEntryId, type: 'answer', message: '', timestamp: new Date().toISOString() }]);

      try {
        const response = await authFetch('/api/local/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message, localDonors: getLocalDonors() })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Something went wrong');

        const finalMessage = data.message || 'Got it.';
        setPageChatEntries(prev => prev.map(e => e.id === aiEntryId ? { ...e, message: finalMessage } : e));
        setPageChatAnimatingId(aiEntryId);
        const wordCount = finalMessage.split(/\s+/).filter(w => w.trim()).length;
        setTimeout(() => setPageChatAnimatingId(null), wordCount * 50 + 500);

        if (data.mutation?.type === 'add') {
          addLocalDonor(data.mutation.donor);
          onRefreshDonors?.();
        } else if (data.mutation?.type === 'update') {
          updateLocalDonor(data.mutation.name, data.mutation.updates);
          onRefreshDonors?.();
        }

        const now = Date.now();
        const feedQuestionEntry = { id: crypto.randomUUID ? crypto.randomUUID() : `q-${now}`, type: 'question', message, timestamp: new Date(now).toISOString() };
        const feedAnswerEntry = { id: crypto.randomUUID ? crypto.randomUUID() : `a-${now + 1}`, type: 'answer', message: finalMessage, status: 'received', timestamp: new Date(now + 1).toISOString() };
        setFeedEntries(prev => [feedAnswerEntry, feedQuestionEntry, ...prev]);
        saveFeedEntryToDb(feedQuestionEntry);
        saveFeedEntryToDb(feedAnswerEntry);
      } catch (error) {
        console.error('Local page chat error:', error);
        setPageChatEntries(prev => prev.map(e => e.id === aiEntryId ? { ...e, message: 'Sorry, something went wrong.' } : e));
      } finally {
        setPageChatStreaming(null);
      }
      return;
    }

    // Build context-aware prompt - ask for CONCISE answers
    // Only wrap as a "Question:" when it actually looks like one - wrapping a plain
    // statement like "she wants coffee next week" this way biases the classifier
    // toward answering it instead of recognizing it as a note to save (update_person).
    const looksLikeQuestion = /\?\s*$/.test(message.trim()) ||
      /^(what|who|when|where|why|how|is|are|does|do|did|can|could|should|would)\b/i.test(message.trim());

    let contextPrompt = message;
    const conciseInstruction = '\n\n[IMPORTANT: Give a very brief, 1-2 sentence answer. Be direct and concise.]';
    if (pageContext.type === 'profile' && pageContext.data) {
      contextPrompt = looksLikeQuestion
        ? `[Context: Viewing ${pageContext.name}'s profile. Data: ${JSON.stringify(pageContext.data)}]${conciseInstruction}\n\nQuestion: ${message}`
        : message;
    } else if (pageContext.type === 'group' && pageContext.members) {
      contextPrompt = `[Context: Group "${pageContext.name}" - ${pageContext.members.length} members: ${pageContext.members.map(m => m.name).join(', ')}. ${pageContext.context || ''}]${conciseInstruction}\n\nQuestion: ${message}`;
    } else if (pageContext.type === 'list') {
      const groupNames = pageContext.groups?.map(g => g.name).join(', ') || 'no groups';
      contextPrompt = `[Context: Viewing list "${pageContext.name}" with groups: ${groupNames}]${conciseInstruction}\n\nQuestion: ${message}`;
    } else if (pageContext.type === 'table') {
      contextPrompt = `[Context: Viewing table "${pageContext.name}" with ${pageContext.rows} rows]${conciseInstruction}\n\nQuestion: ${message}`;
    } else {
      contextPrompt = `${conciseInstruction}\n\nQuestion: ${message}`;
    }

    // Add placeholder for AI response
    const aiEntryId = `pc-ai-${Date.now()}`;
    setPageChatStreaming(aiEntryId);
    setPageChatEntries(prev => [...prev, {
      id: aiEntryId,
      type: 'answer',
      message: '',
      timestamp: new Date().toISOString()
    }]);

    // Local Mode: this page-level chat bar (used on group/list/table/profile
    // pages) previously always called the Supabase-backed /api/chat below,
    // regardless of Local Mode - meaning it could answer using real account
    // donors mixed with locally-mentioned ones. Route to /api/local/chat
    // instead so it only ever sees local data, same as the main feed already does.
    if (isLocalModeEnabled()) {
      try {
        const pageChatHistory = pageChatEntries
          .filter(e => e.message)
          .map(e => ({
            role: e.type === 'question' ? 'user' : 'assistant',
            content: e.message
          }));

        const response = await authPost('/api/local/chat', {
          message,
          localDonors: getLocalDonors(),
          conversationHistory: pageChatHistory.slice(-10)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Something went wrong');

        if (data.mutation?.type === 'add') {
          addLocalDonor(data.mutation.donor);
          onRefreshDonors?.();
        } else if (data.mutation?.type === 'update') {
          updateLocalDonor(data.mutation.name, data.mutation.updates);
          onRefreshDonors?.();
        }

        const finalMessage = data.message || 'Got it.';
        setPageChatEntries(prev => prev.map(e =>
          e.id === aiEntryId ? { ...e, message: finalMessage } : e
        ));
        setPageChatAnimatingId(aiEntryId);
        const wordCount = finalMessage.split(/\s+/).filter(w => w.trim()).length;
        setTimeout(() => setPageChatAnimatingId(null), wordCount * 50 + 500);

        const now = Date.now();
        const feedQuestionEntry = {
          id: crypto.randomUUID ? crypto.randomUUID() : `q-${now}`,
          type: 'question',
          message,
          timestamp: new Date(now).toISOString()
        };
        const feedAnswerEntry = {
          id: crypto.randomUUID ? crypto.randomUUID() : `a-${now + 1}`,
          type: 'answer',
          message: finalMessage,
          status: 'received',
          timestamp: new Date(now + 1).toISOString()
        };
        setFeedEntries(prev => [feedAnswerEntry, feedQuestionEntry, ...prev]);
        saveFeedEntryToDb(feedQuestionEntry);
        saveFeedEntryToDb(feedAnswerEntry);
      } catch (error) {
        console.error('Local pagechat error:', error);
        setPageChatEntries(prev => prev.map(e =>
          e.id === aiEntryId ? { ...e, message: 'Sorry, something went wrong.' } : e
        ));
      } finally {
        setPageChatStreaming(null);
      }
      return;
    }

    try {
      // Build conversation history from pagechat entries
      const pageChatHistory = pageChatEntries
        .filter(e => e.message)
        .map(e => ({
          role: e.type === 'question' ? 'user' : 'assistant',
          content: e.message
        }));

      const response = await authFetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: contextPrompt,
          conversationHistory: pageChatHistory.slice(-10)
        })
      });

      if (response.ok && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let finalMessage = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // A single SSE event can be split across two reads by the underlying TCP
          // stream - buffer any incomplete trailing line instead of dropping it.
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'done' && data.message) {
                  finalMessage = data.message;
                  setPageChatEntries(prev => prev.map(e =>
                    e.id === aiEntryId ? { ...e, message: finalMessage } : e
                  ));
                } else if (data.type === 'narrative_update') {
                  // Push narrative update to profile via shared state
                  console.log('[PageChat] Narrative update for', data.personName);
                  setNarrativeUpdate({
                    personName: data.personName,
                    narrative: data.narrative
                  });
                  setTimeout(() => setNarrativeUpdate(null), 5000);
                } else if (data.type === 'details_update') {
                  // Push details update to profile
                  setNarrativeUpdate(prev => ({
                    ...prev,
                    personName: data.personName,
                    keyDetails: data.keyDetails,
                    tasks: data.tasks
                  }));
                }
              } catch (e) {}
            }
          }
        }

        // If we didn't get a message from streaming, show error
        if (!finalMessage) {
          finalMessage = 'Done.';
          setPageChatEntries(prev => prev.map(e =>
            e.id === aiEntryId ? { ...e, message: finalMessage } : e
          ));
        }

        // Trigger word reveal animation
        setPageChatAnimatingId(aiEntryId);
        const wordCount = finalMessage.split(/\s+/).filter(w => w.trim()).length;
        setTimeout(() => setPageChatAnimatingId(null), wordCount * 50 + 500);

        // Update highlighted people on People page
        if (currentView === 'people') {
          const mentioned = extractMentionedPeople(finalMessage);
          setHighlightedPeople(mentioned);
        }

        // Also add to the main feed so all interactions are tracked (and persist to DB)
        const now = Date.now();
        const feedQuestionEntry = {
          id: crypto.randomUUID ? crypto.randomUUID() : `q-${now}`,
          type: 'question',
          message: message,
          timestamp: new Date(now).toISOString()
        };
        const feedAnswerEntry = {
          id: crypto.randomUUID ? crypto.randomUUID() : `a-${now + 1}`,
          type: 'answer',
          message: finalMessage,
          status: 'received',
          timestamp: new Date(now + 1).toISOString()
        };
        setFeedEntries(prev => [feedAnswerEntry, feedQuestionEntry, ...prev]);
        saveFeedEntryToDb(feedQuestionEntry);
        saveFeedEntryToDb(feedAnswerEntry);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Pagechat error:', error);
      setPageChatEntries(prev => prev.map(e =>
        e.id === aiEntryId ? { ...e, message: 'Sorry, something went wrong.' } : e
      ));
    } finally {
      setPageChatStreaming(null);
    }
  };

  // Wrap handleSubmit - use pagechat for all page views, feed chat for main feed
  const handleSubmit = async (message) => {
    if (currentView === null) {
      // Main feed - use original handler
      return originalHandleSubmit(message);
    }
    // All page views - use pagechat handler (fast, concise responses)
    return handlePageChatSubmit(message);
  };

  // Sync props to local state
  useEffect(() => {
    setLocalGroups(groups || []);
  }, [groups]);

  useEffect(() => {
    setLocalLists(savedLists || []);
  }, [savedLists]);

  // Stale-while-revalidate: When viewing a list, show cached data immediately
  // but fetch fresh data in background and update if changed
  useEffect(() => {
    if (currentView !== 'list' || !viewData?.id) return;

    const refreshListData = async () => {
      try {
        const response = await authFetch('/api/lists?mode=real');
        if (response.ok) {
          const data = await response.json();
          const freshList = data.lists?.find(l => l.id === viewData.id);

          if (freshList) {
            // Check if groups have changed (compare by JSON string for simplicity)
            const currentGroups = JSON.stringify(viewData.groups || []);
            const freshGroups = JSON.stringify(freshList.groups || []);

            if (currentGroups !== freshGroups) {
              console.log('[List Refresh] Updating list with fresh data');
              // Update viewData with fresh list data
              setViewData(prev => ({
                ...prev,
                groups: freshList.groups,
                name: freshList.name || prev.name
              }));
              // Also update localLists
              setLocalLists(prev => prev.map(l =>
                l.id === freshList.id ? { ...l, groups: freshList.groups } : l
              ));
            }
          }
        }
      } catch (err) {
        console.error('[List Refresh] Failed to fetch fresh data:', err);
      }
    };

    // Small delay to let any pending updates complete
    const timeoutId = setTimeout(refreshListData, 500);
    return () => clearTimeout(timeoutId);
  }, [currentView, viewData?.id]);

  // Note: Tables are loaded from API in the useEffect below, not from the savedTables prop
  // The savedTables prop from App.js is always empty since tables are in a separate Supabase table

  // Load tables from API on mount (or from local storage in Local Mode, which
  // has no Supabase data at all)
  useEffect(() => {
    const loadTables = async () => {
      if (isLocalModeEnabled()) {
        setLocalTables(getLocalTables());
        return;
      }
      try {
        const response = await authFetch('/api/tables?mode=real');
        if (response.ok) {
          const data = await response.json();
          console.log('[DesktopApp] Loaded tables from API:', data.tables?.length || 0, 'tables');
          if (data.tables) {
            setLocalTables(data.tables);
          }
        }
      } catch (error) {
        console.error('Failed to load tables:', error);
      }
    };
    loadTables();
  }, []);

  // Load user context on mount
  useEffect(() => {
    refreshUserContext();
  }, []);

  // Scroll pagechat to bottom when entries change
  useEffect(() => {
    if (pageChatHistoryRef.current && pageChatEntries.length > 0) {
      pageChatHistoryRef.current.scrollTop = pageChatHistoryRef.current.scrollHeight;
    }
  }, [pageChatEntries]);

  // Handle note from profile - send with explicit update instruction
  const handleSubmitNoteFromProfile = async (message, donorName) => {
    // Check if this is the first note (sidebar tour should start when leaving profile)
    const isFirstNote = showFirstPersonHint && donors && Object.keys(donors).length === 1;

    // Dismiss the first person hint after submission
    if (showFirstPersonHint) {
      setShowFirstPersonHint(false);
    }

    // Be explicit that this is a note to add to an existing person's profile
    const contextMessage = donorName
      ? `Update ${donorName}'s profile with this note: ${message}`
      : message;
    setChatModalOpen(true);
    await originalHandleSubmit(contextMessage);

    // Mark ready for sidebar tour - will trigger when user navigates away from profile
    if (isFirstNote) {
      setReadyForSidebarTour(true);
    }
  };

  // Check if text is a question (for profile page chat handling)
  const isQuestion = (text) => {
    const trimmed = text.trim().toLowerCase();
    const questionWords = ['who', 'what', 'when', 'where', 'why', 'how', 'is', 'are', 'do', 'does', 'can', 'could', 'would', 'should', 'tell me'];
    return trimmed.endsWith('?') || questionWords.some(w => trimmed.startsWith(w + ' '));
  };

  // Handle user context save (from onboarding or settings)
  const handleSaveUserContext = async (context) => {
    try {
      const response = await authFetch('/api/user-context', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userContext: context, onboardingComplete: true })
      });
      if (response.ok) {
        const data = await response.json();
        setUserContext(data.userContext);
        setOnboardingComplete(true);
        console.log('[DesktopApp] User context saved:', context);
      }
    } catch (error) {
      console.error('Failed to save user context:', error);
    }
  };

  // Handle name tap in feed
  const handleNameTap = (donorName) => {
    navigateTo('profile', { name: donorName });
  };

  // Handle editing group context - open modal
  const handleEditGroupContext = (group) => {
    setEditContextValue(group.context || '');
    setEditContextModal({ open: true, group });
    setTimeout(() => editContextInputRef.current?.focus(), 100);
  };

  // Handle creating a new group - open modal
  const handleCreateNewGroup = () => {
    setNewGroupName('');
    setNewGroupContext('');
    setIsResearchingNewGroup(false);
    setIsCreatingGroup(false);
    setCreateGroupModal({ open: true });
    setTimeout(() => newGroupNameRef.current?.focus(), 100);
  };

  // Close create group modal
  const handleCloseCreateGroup = () => {
    setCreateGroupModal({ open: false });
    setNewGroupName('');
    setNewGroupContext('');
    setIsResearchingNewGroup(false);
    setIsCreatingGroup(false);
  };

  // Delete a group and all people in it
  const handleDeleteGroup = async (groupId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Delete all donors in this group
      await supabase
        .from('donors')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', session.user.id);

      // Delete the group
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', groupId)
        .eq('user_id', session.user.id);

      if (!error) {
        setLocalGroups(prev => prev.filter(g => g.id !== groupId));
        // If we're viewing this group, go back to groups list
        if (currentView === 'group' && viewData?.id === groupId) {
          navigateTo('groups');
        }
        if (onRefreshDonors) onRefreshDonors();
      }
    } catch (error) {
      console.error('Error deleting group:', error);
    }
  };

  // Delete a list
  const handleDeleteList = async (listId) => {
    // Local Mode: the list never existed in Supabase, so just remove it locally
    if (isLocalModeEnabled()) {
      deleteLocalList(listId);
      setLocalLists(prev => prev.filter(l => l.id !== listId));
      if (currentView === 'list' && viewData?.id === listId) {
        goBack();
      }
      return;
    }
    try {
      const response = await authFetch('/api/lists/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listId, mode: 'real' })
      });

      if (response.ok) {
        setLocalLists(prev => prev.filter(l => l.id !== listId));
        // If we're viewing this list, go back
        if (currentView === 'list' && viewData?.id === listId) {
          goBack();
        }
      }
    } catch (error) {
      console.error('Error deleting list:', error);
    }
  };

  const handleDeleteTable = async (tableId) => {
    // Local Mode: the table never existed in Supabase, so just remove it locally
    if (isLocalModeEnabled()) {
      deleteLocalTable(tableId);
      setLocalTables(prev => prev.filter(t => t.id !== tableId));
      if (currentView === 'table' && viewData?.id === tableId) {
        goBack();
      }
      return;
    }
    try {
      const response = await authFetch('/api/tables/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableId, mode: 'real' })
      });

      if (response.ok) {
        setLocalTables(prev => prev.filter(t => t.id !== tableId));
        // If we're viewing this table, go back
        if (currentView === 'table' && viewData?.id === tableId) {
          goBack();
        }
      }
    } catch (error) {
      console.error('Error deleting table:', error);
    }
  };

  // Save new group
  const handleSaveNewGroup = async () => {
    if (!newGroupName.trim() || !user?.id) return;

    setIsCreatingGroup(true);
    const colors = ['#a78bfa', '#4ade80', '#fbbf24', '#60a5fa', '#f472b6', '#ef4444'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    try {
      const newGroup = isLocalModeEnabled()
        ? addLocalGroup(newGroupName.trim(), randomColor, newGroupContext.trim() || null)
        : await createGroup(user.id, newGroupName.trim(), randomColor, newGroupContext.trim() || null);
      if (newGroup) {
        const formatted = supabaseGroupToAppFormat(newGroup);
        setLocalGroups(prev => [...prev, formatted]);
        handleCloseCreateGroup();
        navigateTo('group', formatted);
      }
    } catch (error) {
      console.error('Error creating group:', error);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Research context for new group using AI
  const handleResearchNewGroupContext = async () => {
    if (!newGroupName.trim()) return;

    setIsResearchingNewGroup(true);
    try {
      const response = await authFetch('/api/research-group-context', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groupName: newGroupName.trim(),
          existingContext: newGroupContext
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.context) {
          setNewGroupContext(data.context);
        }
      }
    } catch (error) {
      console.error('Error researching context:', error);
    } finally {
      setIsResearchingNewGroup(false);
    }
  };

  // Add person modal handlers
  const handleAddPersonSubmit = async (name, info, groupId, personalNote) => {
    if (!name || !user?.id) return;

    // Use the pre-selected group from "Add to Group" flow if no groupId specified
    const targetGroupId = groupId || addPersonToGroup?.id;

    // Process narrative with AI - use different endpoints based on what we have
    let finalNarrative = '';
    const hasResearch = info && info.trim();
    const hasNote = personalNote && personalNote.trim();

    if (hasResearch && hasNote) {
      // We have BOTH research and personal note - use merge-narrative to combine them properly
      try {
        const mergeResponse = await authFetch('/api/merge-narrative', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            research: info.trim(),
            personalNote: personalNote.trim(),
            personName: name
          })
        });
        if (mergeResponse.ok) {
          const data = await mergeResponse.json();
          finalNarrative = data.narrative || `${info.trim()}\n\n${personalNote.trim()}`;
        } else {
          finalNarrative = `${info.trim()}\n\n${personalNote.trim()}`;
        }
      } catch (err) {
        console.error('Error merging narrative:', err);
        finalNarrative = `${info.trim()}\n\n${personalNote.trim()}`;
      }
    } else if (hasNote) {
      // Only personal note - enhance it
      try {
        const enhanceResponse = await authFetch('/api/enhance-note', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            note: personalNote.trim(),
            personName: name
          })
        });
        if (enhanceResponse.ok) {
          const data = await enhanceResponse.json();
          finalNarrative = data.narrative || personalNote.trim();
        } else {
          finalNarrative = personalNote.trim();
        }
      } catch (err) {
        console.error('Error enhancing note:', err);
        finalNarrative = personalNote.trim();
      }
    } else if (hasResearch) {
      // Only research info - use it directly (it's already well-formatted)
      finalNarrative = info.trim();
    }

    // Local Storage Mode: this modal's Save button is a third, separate entry
    // point for adding a person (distinct from both chat handlers) - it never
    // went through /api/chat at all, so it needed its own branch here too.
    // Research/narrative processing above this point already doesn't touch
    // Supabase, so only the actual save step needs to change.
    if (isLocalModeEnabled()) {
      addLocalDonor({ name, narrative: finalNarrative });
      const wasFirstPersonFlow = isFirstPersonFlow;
      if (onRefreshDonors) await onRefreshDonors();
      navigateTo('profile', { name });
      if (wasFirstPersonFlow) {
        setIsFirstPersonFlow(false);
        setTimeout(() => setShowFirstPersonHint(true), 600);
      }
      return;
    }

    // Use direct endpoint to save the person with their narrative
    const response = await authFetch('/api/donors/add-with-group', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name,
        notes: finalNarrative,
        groupId: targetGroupId || null,
        mode: 'real'
      })
    });

    if (response.ok) {
      // Capture isFirstPersonFlow BEFORE any async operations that might cause re-renders
      const wasFirstPersonFlow = isFirstPersonFlow;

      if (onRefreshDonors) {
        await onRefreshDonors();
      }

      // Refresh lists and tables for this group
      if (targetGroupId) {
        setTimeout(() => refreshGroupListsAndTables(targetGroupId), 500);
      }

      navigateTo('profile', { name });

      // If this was the first person flow (onboarding), show chat hint
      console.log('[Onboarding] wasFirstPersonFlow:', wasFirstPersonFlow);
      if (wasFirstPersonFlow) {
        setIsFirstPersonFlow(false);
        // Show chat bar hint after a delay to let the profile load
        setTimeout(() => {
          console.log('[Onboarding] Showing chat hint for:', name);
          setShowFirstPersonHint(true);
        }, 600);
      }
    } else {
      console.error('Failed to add person');
    }
  };

  // Research for Add Person modal - uses same endpoint as Profile Research button
  const handleAddPersonResearch = async (personName) => {
    try {
      const response = await authFetch('/api/donor-insight', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          donorName: personName,
          mode: 'real'
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[AddPerson Research] Result:', data);
        // Returns: {ambiguous: true, options: [...]} or {insight: "..."} or {message: "..."}
        return data;
      } else {
        console.error('[AddPerson Research] API error:', response.status);
        return null;
      }
    } catch (err) {
      console.error('[AddPerson Research] Fetch error:', err);
      return null;
    }
  };

  // Add existing person to a group
  const handleAddExistingToGroup = async (personName, groupId) => {
    if (isLocalModeEnabled()) {
      const updated = addLocalDonorToGroup(personName, groupId);
      if (!updated) throw new Error('Failed to add person to group');
      if (onRefreshDonors) await onRefreshDonors();
      return;
    }

    const response = await authFetch('/api/donors/add-to-group', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        personName,
        groupId
      })
    });

    if (response.ok) {
      // Refresh donors to get updated group membership
      if (onRefreshDonors) {
        await onRefreshDonors();
      }
    } else {
      throw new Error('Failed to add person to group');
    }
  };

  // Open add to group modal
  const handleOpenAddToGroup = (group) => {
    setAddToGroupModal({ open: true, group });
  };

  // Handle "Add New" from the add to group modal
  const handleAddNewFromGroup = () => {
    // Store the group to add person to
    const targetGroup = addToGroupModal.group;
    // Close the add-to-group modal first
    setAddToGroupModal({ open: false, group: null });
    // Then set the target group and open add person modal
    setAddPersonToGroup(targetGroup);
    setAddPersonModalOpen(true);
  };

  // Save the edited context
  const handleSaveContext = async () => {
    const group = editContextModal.group;
    if (!group) return;

    try {
      const response = await authFetch(`/api/groups/${group.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: editContextValue })
      });
      if (response.ok) {
        // Update local state
        setLocalGroups(prev => prev.map(g =>
          g.id === group.id ? { ...g, context: editContextValue } : g
        ));
        // Update viewData if we're viewing this group
        if (currentView === 'group' && viewData?.id === group.id) {
          setViewData({ ...viewData, context: editContextValue });
        }
        setEditContextModal({ open: false, group: null, position: null });
      }
    } catch (error) {
      console.error('Failed to update group context:', error);
    }
  };

  // Close edit context modal
  const handleCloseEditContext = () => {
    setEditContextModal({ open: false, group: null, position: null });
  };

  // Research context using AI
  const handleResearchContext = async () => {
    const group = editContextModal.group;
    if (!group) return;

    setIsResearchingContext(true);
    try {
      // Get members of this group for context
      const groupMembers = Object.entries(donors || {})
        .filter(([name, data]) => data.groupId === group.id || data.group_id === group.id)
        .map(([name]) => ({ name }));

      const response = await authFetch(`/api/groups/${group.id}/research-context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          groupName: group.name,
          currentContext: editContextValue,
          members: groupMembers
        })
      });

      if (response.ok) {
        const data = await response.json();
        setEditContextValue(data.suggestedContext || data.research);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Research failed:', response.status, errorData);
        alert('Research failed: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to research context:', error);
      alert('Research failed: ' + error.message);
    } finally {
      setIsResearchingContext(false);
    }
  };

  // Handle creating lists/tables
  const handleCreateListOrTable = async (type, prompt, group, columns) => {
    try {
      const endpoint = type === 'list' ? '/api/lists' : '/api/tables';
      const localForCreate = isLocalModeEnabled();

      // Server fetches members from Supabase for both lists and tables -
      // except Local Mode, where the client sends its own local donors.
      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          groupId: group?.id,
          groupName: group?.name,
          columns: columns || undefined,
          ...(localForCreate ? { localMode: true, localDonors: getLocalDonors() } : {})
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (type === 'list' && data.list) {
          if (localForCreate) addLocalList(data.list);
          setLocalLists(prev => [...prev, data.list]);
          // Navigate to the new list
          navigateTo('list', data.list);
        } else if (type === 'table' && data.table) {
          if (localForCreate) addLocalTable(data.table);
          setLocalTables(prev => [...prev, data.table]);
          // Navigate to the new table
          navigateTo('table', data.table);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Create failed:', response.status, errorData);
        alert('Failed to create: ' + (errorData.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to create:', error);
      alert('Failed to create: ' + error.message);
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (searchButtonRef.current) {
          const rect = searchButtonRef.current.getBoundingClientRect();
          setSearchPosition({ top: rect.top, left: rect.right - 30 });
        }
        setIsSearchOpen(true);
      }
      if (e.key === 'Escape') {
        if (isSearchOpen) {
          setIsSearchOpen(false);
        } else if (chatModalOpen) {
          setChatModalOpen(false);
        } else if (currentView) {
          goBack();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigationHistory, isSearchOpen, currentView, chatModalOpen]);

  // Get context string for chat placeholder
  const getContextString = () => {
    switch (currentView) {
      case 'profile': return viewData?.name ? `about ${viewData.name}` : '';
      case 'group': return viewData?.name ? `about ${viewData.name}` : '';
      case 'people': return 'about your contacts';
      case 'groups': return 'about your groups';
      default: return '';
    }
  };

  // Render the current view content
  const renderCurrentView = () => {
    if (currentView === null) {
      // Feed view
      return (
        <div className="main-feed-view">
          <DesktopFeed
            entries={feedEntries}
            loading={feedLoading}
            donors={donors}
            groups={localGroups}
            streamingText={streamingText}
            streamingEntryId={streamingEntryId}
            onNameTap={handleNameTap}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            placeholder={!onboardingComplete && Object.keys(donors || {}).length === 0 && localGroups.length === 0 ? "Tell me about someone..." : "What happened? Ask anything..."}
            isOnboarding={!onboardingComplete && Object.keys(donors || {}).length === 0 && localGroups.length === 0}
            onGroupSelect={handleGroupSelect}
            onSkipGroupSelect={handleSkipGroupSelect}
            onCreateGroup={handleCreateGroupFromFeed}
            onViewList={handleViewList}
            onCreateListForGroup={handleCreateListForGroup}
            onConfirmAdd={handleConfirmAdd}
            onDeclineAdd={handleDeclineAdd}
            onResearchPerson={handleResearchPerson}
            onSkipResearch={handleSkipResearch}
            onSkipMoreInfo={handleSkipMoreInfo}
            scrollToEntryId={scrollToEntryId}
            onScrollComplete={() => setScrollToEntryId(null)}
            hideInputBar={false}
            onAddPerson={(position) => {
              setAddPersonPosition(position);
              setAddPersonModalOpen(true);
            }}
            // Onboarding props
            showWelcome={!onboardingComplete && Object.keys(donors || {}).length === 0 && localGroups.length === 0}
            onSaveUserContext={handleSaveUserContext}
          />
        </div>
      );
    }

    // Other views with persistent chat bar
    return (
      <div className="main-context-view">
        {/* Header for context views */}
        <div className="context-view-header">
          <button className="context-back-btn" onClick={goBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="context-title">{getViewTitle()}</div>
        </div>

        {/* View content */}
        <div className="context-view-content">
          {renderViewContent()}
        </div>

        {/* Persistent Chat Input Bar - Strategy Mode has its own dedicated dock instead */}
        {currentView !== 'strategy' && (
        <div className={`persistent-chat-bar ${chatInputFocused ? 'expanded' : ''} ${chatModalOpen && pageChatEntries.some(e => e.type === 'answer') ? 'has-response' : ''}`}>
          <div className={`persistent-chat-input-wrapper ${chatbarLoading ? 'loading' : ''}`}>
            {/* Response area - inside wrapper for seamless connection */}
            {chatModalOpen && pageChatEntries.length > 0 && (
              <div className="pagechat-response-area">
                <div className="pagechat-history" ref={pageChatHistoryRef}>
                  {pageChatEntries.map((entry) => (
                    <div key={entry.id} className={`pagechat-entry ${entry.type}`}>
                      {entry.type === 'question' ? (
                        <div className="pagechat-question">{entry.message}</div>
                      ) : entry.message ? (
                        <div className="pagechat-response">
                          {entry.id === pageChatAnimatingId ? (
                            <WordReveal text={entry.message} animate={true} speed={50} />
                          ) : (
                            renderPageChatMessage(entry.message)
                          )}
                        </div>
                      ) : (
                        <div className="pagechat-response">
                          <div className="pagechat-thinking">
                            <span></span><span></span><span></span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button className="pagechat-dismiss" onClick={() => setChatModalOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {/* Onboarding hint for first person */}
            {showFirstPersonHint && currentView === 'profile' && (
              <div className="first-person-hint">
                <div className="first-person-hint-content">
                  <span>Tell me about {viewData?.name} — how you met, what you discussed, anything you want to remember</span>
                  <button className="first-person-hint-dismiss" onClick={() => setShowFirstPersonHint(false)}>×</button>
                </div>
                <div className="first-person-hint-arrow"></div>
              </div>
            )}
            <div className="persistent-chat-input-row">
              <textarea
                ref={chatInputRef}
                rows={1}
                placeholder={currentView === 'profile' && viewData?.name
                  ? `Add note or ask about ${viewData.name}...`
                  : `Ask anything ${getContextString()}...`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && e.target.value.trim()) {
                    e.preventDefault();
                    const message = e.target.value.trim();
                    e.target.value = '';
                    e.target.style.height = 'auto'; // Reset height
                    setChatbarLoading(true);
                    // On profile: questions go through regular chat, notes update the profile
                    if (currentView === 'profile' && viewData?.name) {
                      if (isQuestion(message)) {
                        // Question about the person - use regular chat with context
                        handleSubmit(`${message} (about ${viewData.name})`).finally(() => setChatbarLoading(false));
                      } else {
                        // Note to add to their profile
                        handleSubmitNoteFromProfile(message, viewData.name).finally(() => setChatbarLoading(false));
                      }
                    } else {
                      handleSubmit(message).finally(() => setChatbarLoading(false));
                    }
                  }
                }}
                onInput={(e) => {
                  // Auto-resize textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                }}
                onFocus={() => setChatInputFocused(true)}
                onBlur={() => setChatInputFocused(false)}
              />
              <button
                className="persistent-chat-send"
                onClick={() => {
                  if (chatInputRef.current?.value.trim()) {
                    const message = chatInputRef.current.value.trim();
                    chatInputRef.current.value = '';
                    chatInputRef.current.style.height = 'auto'; // Reset height
                    setChatbarLoading(true);
                    // On profile: questions go through regular chat, notes update the profile
                    if (currentView === 'profile' && viewData?.name) {
                      if (isQuestion(message)) {
                        handleSubmit(`${message} (about ${viewData.name})`).finally(() => setChatbarLoading(false));
                      } else {
                        handleSubmitNoteFromProfile(message, viewData.name).finally(() => setChatbarLoading(false));
                      }
                    } else {
                      handleSubmit(message).finally(() => setChatbarLoading(false));
                    }
                  }
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="send-spinner" />
                ) : (
                  <span>↑</span>
                )}
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
    );
  };

  // Get title for current view
  const getViewTitle = () => {
    switch (currentView) {
      case 'people': return 'People';
      case 'groups': return 'Groups';
      case 'profile': return viewData?.name || 'Profile';
      case 'group': return viewData?.name || 'Group';
      case 'list': return viewData?.name || viewData?.organizationPrompt || 'List';
      case 'table': return viewData?.name || 'Table';
      case 'settings': return 'Settings';
      case 'strategy': return 'Strategy';
      default: return '';
    }
  };

  // Render view content based on currentView
  const renderViewContent = () => {
    switch (currentView) {
      case 'people':
        return (
          <DesktopPeopleBrowse
            donors={donors}
            groups={localGroups}
            onSelectPerson={(name) => navigateTo('profile', { name })}
            onAddPerson={() => {
              const noPeople = !donors || Object.keys(donors).length === 0;
              setIsFirstPersonFlow(noPeople);
              setAddPersonModalOpen(true);
            }}
            highlightedPeople={highlightedPeople}
          />
        );

      case 'strategy':
        return <DesktopStrategyView donors={donors} />;

      case 'groups':
        return (
          <div className="desktop-groups-browse">
            <div className="groups-browse-hero">
              <div className="groups-browse-hero-text">
                <h1 className="groups-browse-title">Your</h1>
                <h2 className="groups-browse-subtitle">groups</h2>
              </div>
              <button className="add-group-btn" onClick={handleCreateNewGroup}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Add Group
              </button>
            </div>
            <div className="groups-browse-grid">
              {localGroups.map(group => {
                const isInGroup = (d) => {
                  if (d.group_ids && Array.isArray(d.group_ids) && d.group_ids.length > 0) {
                    return d.group_ids.some(id => String(id) === String(group.id));
                  }
                  return d.groupId === group.id || d.group_id === group.id;
                };
                const members = Object.entries(donors || {})
                  .filter(([, d]) => isInGroup(d))
                  .map(([name, data]) => ({ name, ...data }))
                  .slice(0, 6);
                const memberCount = Object.values(donors || {}).filter(d => isInGroup(d)).length;
                const groupLists = localLists.filter(l => l.groupId === group.id || l.group_id === group.id).slice(0, 3);
                const groupTables = localTables.filter(t => t.groupId === group.id || t.group_id === group.id).slice(0, 2);
                const pipelineTotal = Object.values(donors || {})
                  .filter(d => isInGroup(d))
                  .reduce((sum, d) => {
                    const amt = parseFloat(String(d.amount || '0').replace(/[^0-9.]/g, '')) || 0;
                    return sum + amt;
                  }, 0);
                const formatPipeline = (amt) => {
                  if (amt >= 1000000) return `$${(amt / 1000000).toFixed(1)}M`;
                  if (amt >= 1000) return `$${Math.round(amt / 1000)}K`;
                  if (amt > 0) return `$${amt}`;
                  return null;
                };
                const getInitials = (name) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                const getPersonColor = (name) => {
                  const colors = ['#8b5cf6', '#06b6d4', '#38bdf8', '#a78bfa', '#ec4899', '#3b82f6', '#14b8a6', '#818cf8'];
                  const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
                  return colors[index];
                };

                return (
                  <button
                    key={group.id}
                    className="groups-hero-card"
                    onClick={() => navigateTo('group', group)}
                  >
                    {/* Member avatars layer */}
                    {members.length > 0 && (
                      <div className="groups-hero-avatars">
                        {members.slice(0, 5).map((member, i) => (
                          <div
                            key={member.name}
                            className="groups-hero-avatar"
                            style={{
                              zIndex: 10 - i,
                              marginLeft: i === 0 ? 0 : -12,
                              borderColor: getPersonColor(member.name),
                              color: getPersonColor(member.name)
                            }}
                          >
                            {getInitials(member.name)}
                          </div>
                        ))}
                        {memberCount > 5 && (
                          <div className="groups-hero-avatar more" style={{ marginLeft: -12, zIndex: 4 }}>
                            +{memberCount - 5}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Group info */}
                    <div className="groups-hero-info">
                      <h3 className="groups-hero-name" style={{ color: group.color || '#a78bfa' }}>{group.name}</h3>
                      <div className="groups-hero-stats">
                        <span>{memberCount} {memberCount === 1 ? 'person' : 'people'}</span>
                        {formatPipeline(pipelineTotal) && (
                          <>
                            <span className="stat-dot">·</span>
                            <span className="pipeline">{formatPipeline(pipelineTotal)}</span>
                          </>
                        )}
                        {(groupLists.length > 0 || groupTables.length > 0) && (
                          <>
                            <span className="stat-dot">·</span>
                            <span>{groupLists.length + groupTables.length} {groupLists.length + groupTables.length === 1 ? 'view' : 'views'}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
              {localGroups.length === 0 && (
                <div className="groups-browse-empty">
                  No groups yet. Create one by asking in the chat.
                </div>
              )}
            </div>
          </div>
        );

      case 'profile':
        return (
          <DesktopProfileView
            donorName={viewData?.name}
            donorData={donors?.[viewData?.name]}
            groups={localGroups}
            onClose={goBack}
            onNavigateToPerson={handleNavigateToPerson}
            dataMode="real"
            narrativeUpdate={narrativeUpdate}
            onRefreshDonors={onRefreshDonors}
          />
        );

      case 'group':
        return (
          <DesktopGroupView
            group={viewData}
            donors={donors}
            savedLists={localLists}
            savedTables={localTables}
            onSelectPerson={(name) => navigateTo('profile', { name })}
            onSelectList={(list) => navigateTo('list', list)}
            onSelectTable={(table) => navigateTo('table', table)}
            onDeleteList={handleDeleteList}
            onShowDeleteConfirm={(type, item, position) => setDeleteConfirmModal({ open: true, type, item, position })}
            onClose={goBack}
            onEditContext={handleEditGroupContext}
            onCreateList={(group) => setCreateModal({ open: true, type: 'list', group })}
            onCreateTable={(group) => setCreateModal({ open: true, type: 'table', group })}
            onAddPerson={handleOpenAddToGroup}
          />
        );

      case 'list':
        const listParentGroup = localGroups.find(g => g.id === viewData?.groupId || g.id === viewData?.group_id);
        const getListInitials = (name) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
        const getListPersonColor = (name) => {
          const colors = ['#8b5cf6', '#06b6d4', '#38bdf8', '#a78bfa', '#ec4899', '#3b82f6', '#14b8a6', '#818cf8'];
          const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
          return colors[index];
        };
        const listGroupColors = ['#38bdf8', '#f472b6', '#ef4444', '#60a5fa', '#a78bfa', '#ec4899'];
        const listSearchLower = listTableSearch.toLowerCase();

        return (
          <div className="list-panel">
            <div className="lp-header">
              <div className="lp-header-row">
                <div className="lp-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
                    <line x1="8" y1="6" x2="21" y2="6"/>
                    <line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                  </svg>
                </div>
                <div>
                  <h2 className="lp-name">{viewData?.name || viewData?.organizationPrompt || 'List'}</h2>
                  {listParentGroup && (
                    <p className="lp-parent">from <span style={{ color: listParentGroup.color || '#a78bfa' }}>{listParentGroup.name}</span></p>
                  )}
                </div>
              </div>
              <div className="lp-search-bar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search members..."
                  value={listTableSearch}
                  onChange={(e) => setListTableSearch(e.target.value)}
                  className="lp-search-input"
                />
                {listTableSearch && (
                  <button className="lp-search-clear" onClick={() => setListTableSearch('')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="lp-content">
              {viewData?.groups?.map((group, gi) => {
                // Filter people by search
                const filteredPeople = listSearchLower
                  ? (group.people || []).filter(person => {
                      const name = typeof person === 'string' ? person : person.name;
                      return name.toLowerCase().includes(listSearchLower);
                    })
                  : group.people || [];

                // Skip groups with no matching people when searching
                if (listSearchLower && filteredPeople.length === 0) return null;
                const groupColor = listGroupColors[gi % listGroupColors.length];
                const totalCount = group.people?.length || 0;
                return (
                  <div key={gi} className="lp-group-section">
                    <div className="lp-group-header">
                      <div className="lp-group-dot" style={{ background: groupColor }} />
                      <span className="lp-group-label">{group.name}</span>
                      <span className="lp-group-count">
                        ({listSearchLower ? `${filteredPeople.length}/${totalCount}` : totalCount})
                      </span>
                    </div>
                    <div className="lp-group-people">
                      {filteredPeople.map((person, pi) => {
                        const personName = typeof person === 'string' ? person : person.name;
                        const originalIndex = (group.people || []).findIndex(p =>
                          (typeof p === 'string' ? p : p.name) === personName
                        );
                        return (
                          <div key={pi} className="lp-person-wrapper">
                            <button
                              className="lp-person-pill"
                              onClick={() => navigateTo('profile', { name: personName })}
                            >
                              <div className="lp-person-avatar" style={{ borderColor: getListPersonColor(personName), color: getListPersonColor(personName) }}>{getListInitials(personName)}</div>
                              <span className="lp-person-name">{personName}</span>
                            </button>
                            <button
                              className="lp-person-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleListPersonRemove(gi, originalIndex, personName);
                              }}
                              title="Remove from list"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'table':
        const tableParentGroup = localGroups.find(g => g.id === viewData?.groupId || g.id === viewData?.group_id);
        const tableColumns = (viewData?.columns || []).map(col => col.name || col);
        const isMoney = (val) => typeof val === 'string' && val.includes('$');
        const tableSearchLower = listTableSearch.toLowerCase();
        const nameColIdx = tableColumns.findIndex(c => c.toLowerCase() === 'name');

        // Filter rows by search (search in Name column)
        const filteredRows = tableSearchLower
          ? (viewData?.rows || []).filter(row => {
              const name = Array.isArray(row)
                ? (row[nameColIdx >= 0 ? nameColIdx : 0] || '')
                : (row.Name || row.name || '');
              return name.toLowerCase().includes(tableSearchLower);
            })
          : viewData?.rows || [];

        return (
          <div className="table-panel">
            <div className="tp-header">
              <div className="tp-header-row">
                <div className="tp-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <line x1="3" y1="9" x2="21" y2="9"/>
                  </svg>
                </div>
                <div>
                  <h2 className="tp-name">{viewData?.name || 'Table'}</h2>
                  <p className="tp-meta">
                    {tableParentGroup && (<>from <span style={{ color: tableParentGroup.color || '#a78bfa' }}>{tableParentGroup.name}</span> · </>)}
                    {tableSearchLower ? `${filteredRows.length}/${viewData?.rows?.length || 0}` : viewData?.rows?.length || 0} rows
                  </p>
                </div>
              </div>
              <div className="lp-search-bar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={listTableSearch}
                  onChange={(e) => setListTableSearch(e.target.value)}
                  className="lp-search-input"
                />
                {listTableSearch && (
                  <button className="lp-search-clear" onClick={() => setListTableSearch('')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="tp-content">
              <table className="tp-table">
                <thead>
                  <tr>
                    {tableColumns.map((col, idx) => (<th key={idx}>{col}</th>))}
                    <th className="tp-actions-header"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, filteredIdx) => {
                    // Get original row index for editing
                    const rowIdx = (viewData?.rows || []).indexOf(row);
                    return (
                    <tr key={rowIdx} className="tp-row">
                      {tableColumns.map((col, colIdx) => {
                        // Handle both array rows (from AI) and object rows (legacy)
                        const value = Array.isArray(row)
                          ? (row[colIdx] || '—')
                          : (row[col] || row[col.toLowerCase()] || '—');
                        const isNameCol = col.toLowerCase() === 'name';
                        const isMoneyVal = isMoney(value);
                        const isEditing = editingCell?.rowIdx === rowIdx && editingCell?.colIdx === colIdx;

                        return (
                          <td
                            key={colIdx}
                            className={`tp-cell ${isNameCol ? 'name-cell' : 'editable-cell'} ${isMoneyVal ? 'money-cell' : ''} ${isEditing ? 'editing' : ''}`}
                            onClick={isNameCol && value !== '—'
                              ? () => navigateTo('profile', { name: value })
                              : !isEditing ? () => handleTableCellClick(rowIdx, colIdx, value, isNameCol) : undefined
                            }
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                className="tp-cell-input"
                                value={editingCell.value}
                                onChange={(e) => setEditingCell(prev => ({ ...prev, value: e.target.value }))}
                                onBlur={handleTableCellSave}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleTableCellSave();
                                  if (e.key === 'Escape') setEditingCell(null);
                                }}
                                autoFocus
                              />
                            ) : (
                              value
                            )}
                          </td>
                        );
                      })}
                      <td className="tp-cell tp-actions-cell">
                        <button
                          className="tp-delete-btn"
                          onClick={() => handleTableRowDelete(rowIdx)}
                          title="Delete row"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'settings':
        return (
          <DesktopSettings
            user={user}
            subscription={subscription}
            onSignOut={onSignOut}
            onClose={goBack}
            userContext={userContext}
            onSaveUserContext={handleSaveUserContext}
            donors={donors}
            groups={localGroups}
            onImportPeople={() => setImportModalOpen(true)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="desktop-app">
      {/* Sidebar */}
      <div className={`desktop-sidebar ${sidebarOpen ? 'expanded' : ''}`}>
        <div className="sidebar-nav">
          <button
            className="sidebar-icon-btn sidebar-nav-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 3v18" />
            </svg>
            <span className="nav-label nav-brand">knowroom</span>
          </button>
          <button
            ref={searchButtonRef}
            className="sidebar-icon-btn sidebar-nav-btn"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setSearchPosition({ top: rect.top, left: rect.right - 30 });
              setIsSearchOpen(true);
            }}
            title="Search"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <span className="nav-label">Search</span>
          </button>
          <button
            className={`sidebar-icon-btn sidebar-nav-btn has-add-btn ${currentView === 'people' || currentView === 'profile' ? 'active' : ''}`}
            onClick={() => navigateTo('people')}
            title="People"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
            </svg>
            <span className="nav-label">People</span>
            <span
              className="sidebar-add-btn"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                setAddChoicePosition({ top: rect.top - 10, left: rect.right + 8 });
                setAddChoiceModalOpen(true);
              }}
              title="Add Person or Import"
            >+</span>
          </button>
          <button
            ref={chatButtonRef}
            className={`sidebar-icon-btn sidebar-nav-btn ${currentView === null ? 'active' : ''} ${sidebarTourStep === 1 ? 'tour-highlight' : ''}`}
            onClick={() => navigateTo('feed')}
            title="Chat"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <span className="nav-label">Chat</span>
          </button>
          <div className="sidebar-groups-nav-wrapper">
            <button
              ref={groupsButtonRef}
              className={`sidebar-icon-btn sidebar-nav-btn ${currentView === 'groups' || currentView === 'group' ? 'active' : ''} ${sidebarTourStep === 2 ? 'tour-highlight' : ''}`}
              onClick={() => {
                navigateTo('groups');
                if (showGroupsHint) setShowGroupsHint(false);
                if (sidebarTourStep === 2) setSidebarTourStep(0);
              }}
              title="Groups"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              <span className="nav-label">Groups</span>
            </button>
            {showGroupsHint && (
              <div className="sidebar-hint" onClick={() => { navigateTo('groups'); setShowGroupsHint(false); }}>
                <span className="sidebar-hint-text">Visit your first group</span>
                <span className="sidebar-hint-arrow">→</span>
              </div>
            )}
          </div>

          {sidebarOpen && localGroups.length > 0 && (
          <div className="sidebar-groups-list">
            {localGroups.map(group => {
              const groupLists = localLists.filter(l => l.groupId === group.id || l.group_id === group.id);
              const groupTables = localTables.filter(t => t.groupId === group.id || t.group_id === group.id);
              return (
                <div key={group.id} className="sidebar-group-container">
                  <div className="sidebar-group-row">
                    <button
                      className={`sidebar-group-item ${currentView === 'group' && viewData?.id === group.id ? 'active' : ''}`}
                      onClick={() => navigateTo('group', group)}
                    >
                      {group.name}
                    </button>
                    <button
                      className="sidebar-group-menu"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setGroupMenuModal({
                          open: true,
                          group,
                          position: { top: rect.top, left: rect.right + 8 }
                        });
                      }}
                      title="Group options"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <circle cx="5" cy="12" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="19" cy="12" r="2"/>
                      </svg>
                    </button>
                  </div>
                  {(groupLists.length > 0 || groupTables.length > 0) && (
                    <div className="sidebar-group-data">
                      {groupLists.map(list => (
                        <button
                          key={list.id}
                          className={`sidebar-data-item ${currentView === 'list' && viewData?.id === list.id ? 'active' : ''}`}
                          onClick={() => navigateTo('list', list)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"/>
                            <line x1="8" y1="12" x2="21" y2="12"/>
                            <line x1="8" y1="18" x2="21" y2="18"/>
                            <line x1="3" y1="6" x2="3.01" y2="6"/>
                            <line x1="3" y1="12" x2="3.01" y2="12"/>
                            <line x1="3" y1="18" x2="3.01" y2="18"/>
                          </svg>
                          {list.name || list.title || 'Untitled List'}
                          <span
                            className="sidebar-item-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setDeleteConfirmModal({
                                open: true,
                                type: 'list',
                                item: list,
                                position: { top: rect.top - 50, left: rect.right + 8 }
                              });
                            }}
                          >
                            ×
                          </span>
                        </button>
                      ))}
                      {groupTables.map(table => (
                        <button
                          key={table.id}
                          className={`sidebar-data-item ${currentView === 'table' && viewData?.id === table.id ? 'active' : ''}`}
                          onClick={() => navigateTo('table', table)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                            <line x1="3" y1="9" x2="21" y2="9"/>
                            <line x1="3" y1="15" x2="21" y2="15"/>
                            <line x1="9" y1="3" x2="9" y2="21"/>
                          </svg>
                          {table.name || table.title || 'Untitled Table'}
                          <span
                            className="sidebar-item-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setDeleteConfirmModal({
                                open: true,
                                type: 'table',
                                item: table,
                                position: { top: rect.top - 50, left: rect.right + 8 }
                              });
                            }}
                          >
                            ×
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}

          <button
            className={`sidebar-icon-btn sidebar-nav-btn ${currentView === 'strategy' ? 'active' : ''}`}
            onClick={() => navigateTo('strategy')}
            title="Strategy"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="6" cy="6" r="3"/>
              <circle cx="18" cy="6" r="3"/>
              <circle cx="12" cy="18" r="3"/>
              <path d="M8.5 7.5L15.5 16M15.5 7.5L8.5 16"/>
            </svg>
            <span className="nav-label">Strategy</span>
          </button>
        </div>

        <div className="sidebar-spacer" />

        <div className="sidebar-bottom-buttons">
          <button
            ref={upcomingButtonRef}
            className="sidebar-icon-btn"
            onClick={() => {
              const rect = upcomingButtonRef.current?.getBoundingClientRect();
              if (rect) {
                setUpcomingPosition({ top: 80, left: rect.right + 8 });
              }
              setUpcomingModalOpen(true);
            }}
            title="Upcoming"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <span className="nav-label">Upcoming</span>
          </button>

          <button
            ref={helpButtonRef}
            className="sidebar-icon-btn"
            onClick={() => {
              const rect = helpButtonRef.current?.getBoundingClientRect();
              if (rect) {
                setHelpModalPosition({ top: rect.top - 350, left: rect.right + 8 });
              }
              setShowHelpModal(true);
            }}
            title="Help"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9 9a3 3 0 1 1 4 2.83c-.5.29-1 .8-1 1.42V14" strokeLinecap="round" />
              <circle cx="12" cy="17.5" r="0.5" fill="currentColor" stroke="none" />
            </svg>
            <span className="nav-label">Help</span>
          </button>

          <button className="sidebar-icon-btn" onClick={() => navigateTo('settings')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
            <span className="nav-label">Settings</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="desktop-main">
        {renderCurrentView()}
      </div>

      {/* Search Modal */}
      <DesktopSearchModal
        isOpen={isSearchOpen}
        onClose={() => { setIsSearchOpen(false); setSearchPosition(null); }}
        position={searchPosition}
        donors={donors}
        groups={localGroups}
        savedLists={localLists}
        savedTables={localTables}
        feedEntries={feedEntries}
        onNavigate={(type, data, extra) => {
          if (type === 'feed' && extra?.scrollToEntryId) {
            navigateTo('feed');
            setTimeout(() => setScrollToEntryId(extra.scrollToEntryId), 150);
          } else {
            navigateTo(type, data);
          }
        }}
      />

      {/* Upcoming Modal (Tasks + Meetings) */}
      {upcomingModalOpen && (
        <div className="search-modal-backdrop" onClick={() => { setUpcomingModalOpen(false); setUpcomingPosition(null); }}>
          <div
            className="search-modal upcoming-modal"
            style={upcomingPosition ? {
              position: 'fixed',
              top: upcomingPosition.top,
              left: upcomingPosition.left
            } : undefined}
            onClick={e => e.stopPropagation()}
          >
            <div className="search-modal-header">
              <svg className="search-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              <h2 style={{ fontSize: '22px', fontWeight: 600, margin: 0, flex: 1, color: '#fff' }}>
                Upcoming
              </h2>
              <button className="search-modal-close" onClick={() => { setUpcomingModalOpen(false); setUpcomingPosition(null); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Toggle tabs */}
            <div className="upcoming-tabs">
              <button
                className={`upcoming-tab ${upcomingTab === 'meetings' ? 'active' : ''}`}
                onClick={() => setUpcomingTab('meetings')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Meetings
                {extractedMeetings.length > 0 && <span className="upcoming-tab-count">{extractedMeetings.length}</span>}
              </button>
              <button
                className={`upcoming-tab ${upcomingTab === 'tasks' ? 'active' : ''}`}
                onClick={() => setUpcomingTab('tasks')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4"/>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
                Tasks
                {extractedTasks.length > 0 && <span className="upcoming-tab-count">{extractedTasks.length}</span>}
              </button>
            </div>

            {/* Search bar */}
            <div className="upcoming-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search..."
                value={upcomingSearch}
                onChange={(e) => setUpcomingSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div className="search-modal-results">
              {upcomingTab === 'tasks' && (() => {
                const filteredTasks = extractedTasks.filter(item =>
                  !upcomingSearch ||
                  item.task?.toLowerCase().includes(upcomingSearch.toLowerCase()) ||
                  item.person?.toLowerCase().includes(upcomingSearch.toLowerCase())
                );
                return filteredTasks.length === 0 ? (
                  <div className="search-modal-empty">
                    {upcomingSearch ? 'No matching tasks' : 'No tasks found in your contacts\' profiles'}
                  </div>
                ) : (
                  <div className="search-modal-section">
                    {filteredTasks.map((item, idx) => (
                      <button
                        key={`${upcomingTab}-${idx}`}
                        className="search-modal-result"
                        style={{ animationDelay: `${idx * 50}ms` }}
                        onClick={() => {
                          navigateTo('profile', { name: item.person });
                          setUpcomingModalOpen(false);
                          setUpcomingPosition(null);
                          setUpcomingSearch('');
                        }}
                      >
                        <div className="result-avatar" style={{ background: '#8b5cf6' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 11l3 3L22 4"/>
                          </svg>
                        </div>
                        <div className="result-info">
                          <div className="result-name" style={{ fontSize: '15px' }}>{item.task}</div>
                          <div className="result-meta">{item.person}{item.due ? ` • ${formatRelativeDate(item.due)}` : ''}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}

              {upcomingTab === 'meetings' && (() => {
                const filteredMeetings = extractedMeetings.filter(item =>
                  !upcomingSearch ||
                  item.meeting?.toLowerCase().includes(upcomingSearch.toLowerCase()) ||
                  item.person?.toLowerCase().includes(upcomingSearch.toLowerCase())
                );
                return filteredMeetings.length === 0 ? (
                  <div className="search-modal-empty">
                    {upcomingSearch ? 'No matching meetings' : 'No meetings found in your contacts\' profiles'}
                  </div>
                ) : (
                  <div className="search-modal-section">
                    {filteredMeetings.map((item, idx) => (
                      <button
                        key={`${upcomingTab}-${idx}`}
                        className="search-modal-result"
                        style={{ animationDelay: `${idx * 50}ms` }}
                        onClick={() => {
                          navigateTo('profile', { name: item.person });
                          setUpcomingModalOpen(false);
                          setUpcomingPosition(null);
                          setUpcomingSearch('');
                        }}
                      >
                        <div className="result-avatar" style={{ background: '#a78bfa' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                        </div>
                        <div className="result-info">
                          <div className="result-name" style={{ fontSize: '15px' }}>{item.meeting}</div>
                          <div className="result-meta">{item.person}{item.date ? ` • ${formatRelativeDate(item.date)}` : ''}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Group Menu Modal */}
      {groupMenuModal.open && (
        <div className="group-menu-backdrop" onClick={() => setGroupMenuModal({ open: false, group: null, position: null })}>
          <div
            className="group-menu-modal"
            style={{
              position: 'fixed',
              top: groupMenuModal.position?.top || 100,
              left: groupMenuModal.position?.left || 100
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="group-menu-header">
              <h3 className="group-menu-title">{groupMenuModal.group?.name}</h3>
            </div>
            <div className="group-menu-options">
              <button
                className="group-menu-option"
                onClick={() => {
                  const position = groupMenuModal.position;
                  setGroupMenuModal({ open: false, group: null, position: null });
                  setAddPersonToGroup(groupMenuModal.group);
                  setAddPersonPosition(position);
                  setAddPersonModalOpen(true);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
                Add Person
              </button>
              <button
                className="group-menu-option"
                onClick={() => {
                  const group = groupMenuModal.group;
                  setGroupMenuModal({ open: false, group: null, position: null });
                  setCreateModal({ open: true, type: 'list', group });
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6"/>
                  <line x1="8" y1="12" x2="21" y2="12"/>
                  <line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/>
                  <line x1="3" y1="12" x2="3.01" y2="12"/>
                  <line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
                Add List
              </button>
              <button
                className="group-menu-option"
                onClick={() => {
                  const group = groupMenuModal.group;
                  setGroupMenuModal({ open: false, group: null, position: null });
                  setCreateModal({ open: true, type: 'table', group });
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <line x1="3" y1="9" x2="21" y2="9"/>
                  <line x1="3" y1="15" x2="21" y2="15"/>
                  <line x1="9" y1="3" x2="9" y2="21"/>
                </svg>
                Add Table
              </button>
              <button
                className="group-menu-option"
                onClick={() => {
                  const position = groupMenuModal.position;
                  setGroupMenuModal({ open: false, group: null, position: null });
                  // Open edit context at the same position
                  setEditContextValue(groupMenuModal.group?.context || '');
                  setEditContextModal({ open: true, group: groupMenuModal.group, position });
                  setTimeout(() => editContextInputRef.current?.focus(), 100);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit Context
              </button>
              <button
                className="group-menu-option danger"
                onClick={() => {
                  const position = groupMenuModal.position;
                  setGroupMenuModal({ open: false, group: null, position: null });
                  setDeleteConfirmModal({ open: true, type: 'group', item: groupMenuModal.group, position });
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete Group
              </button>
            </div>
            <button
              className="group-menu-cancel"
              onClick={() => setGroupMenuModal({ open: false, group: null })}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.open && (
        <div className="group-menu-backdrop" onClick={() => setDeleteConfirmModal({ open: false, type: null, item: null, position: null })}>
          <div
            className="group-menu-modal delete-confirm wide"
            style={{
              position: 'fixed',
              top: deleteConfirmModal.position?.top || 100,
              left: deleteConfirmModal.position?.left || 100,
              maxWidth: `calc(100vw - ${(deleteConfirmModal.position?.left || 100) + 20}px)`
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="group-menu-header">
              <h3 className="group-menu-title delete">
                Delete "{deleteConfirmModal.item?.name || deleteConfirmModal.item?.title || 'Untitled'}"?
              </h3>
            </div>
            <p className="delete-confirm-message">
              {deleteConfirmModal.type === 'group' && 'This will delete the group and all people in it.'}
              {deleteConfirmModal.type === 'list' && 'This will permanently delete this list.'}
              {deleteConfirmModal.type === 'table' && 'This will permanently delete this table.'}
            </p>
            <div className="group-menu-options">
              <button
                className="group-menu-option danger"
                onClick={() => {
                  if (deleteConfirmModal.type === 'group') {
                    handleDeleteGroup(deleteConfirmModal.item?.id);
                  } else if (deleteConfirmModal.type === 'list') {
                    handleDeleteList(deleteConfirmModal.item?.id);
                  } else if (deleteConfirmModal.type === 'table') {
                    handleDeleteTable(deleteConfirmModal.item?.id);
                  }
                  setDeleteConfirmModal({ open: false, type: null, item: null, position: null });
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            </div>
            <button
              className="group-menu-cancel"
              onClick={() => setDeleteConfirmModal({ open: false, type: null, item: null, position: null })}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {createGroupModal.open && (
        <div className="edit-context-modal-backdrop" onClick={handleCloseCreateGroup}>
          <div className="edit-context-modal" onClick={e => e.stopPropagation()}>
            <div className="edit-context-modal-header">
              <svg className="edit-context-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              <div className="edit-context-modal-title">
                <h3>New Group</h3>
                <span>Create a new group</span>
              </div>
              <button className="edit-context-modal-close" onClick={handleCloseCreateGroup}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="edit-context-modal-body">
              <input
                ref={newGroupNameRef}
                type="text"
                className="create-group-name-input"
                placeholder="Group name..."
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleSaveNewGroup();
                  }
                  if (e.key === 'Escape') {
                    handleCloseCreateGroup();
                  }
                }}
              />
              <textarea
                className="edit-context-textarea"
                placeholder="Describe this group... What are they focused on? What's important to know? (optional)"
                value={newGroupContext}
                onChange={(e) => setNewGroupContext(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleSaveNewGroup();
                  }
                  if (e.key === 'Escape') {
                    handleCloseCreateGroup();
                  }
                }}
              />
            </div>
            <div className="edit-context-modal-footer">
              <span className="edit-context-hint">
                <kbd>⌘</kbd><kbd>Enter</kbd> to create
              </span>
              <div className="edit-context-actions">
                <button
                  className="edit-context-research"
                  onClick={handleResearchNewGroupContext}
                  disabled={isResearchingNewGroup || !newGroupName.trim()}
                >
                  {isResearchingNewGroup ? (
                    <>
                      <span className="research-spinner"></span>
                      Researching...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                      AI Research
                    </>
                  )}
                </button>
                <button className="edit-context-cancel" onClick={handleCloseCreateGroup}>
                  Cancel
                </button>
                <button
                  className="edit-context-save"
                  onClick={handleSaveNewGroup}
                  disabled={!newGroupName.trim() || isCreatingGroup}
                >
                  {isCreatingGroup ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Person Modal */}
      <DesktopAddPersonModal
        isOpen={addPersonModalOpen}
        onClose={() => {
          setAddPersonModalOpen(false);
          setAddPersonToGroup(null);
          setAddPersonPosition(null);
          setAddPersonPrefill({ name: '', note: '' });
        }}
        groups={localGroups}
        onSubmit={handleAddPersonSubmit}
        onResearch={handleAddPersonResearch}
        onCreateGroup={async (name, context) => {
          const colors = ['#a78bfa', '#4ade80', '#fbbf24', '#60a5fa', '#f472b6', '#ef4444'];
          const randomColor = colors[Math.floor(Math.random() * colors.length)];
          const newGroup = isLocalModeEnabled()
            ? addLocalGroup(name, randomColor, context || null)
            : await createGroup(user.id, name, randomColor, context || null);
          if (newGroup) {
            const formatted = supabaseGroupToAppFormat(newGroup);
            setLocalGroups(prev => [...prev, formatted]);
            return formatted;
          }
          return null;
        }}
        preselectedGroupId={addPersonToGroup?.id}
        position={addPersonPosition}
        prefillName={addPersonPrefill.name}
        prefillNote={addPersonPrefill.note}
        isOnboarding={isFirstPersonFlow}
      />

      {/* Onboarding: Add to Group Prompt */}
      {onboardingGroupPrompt.open && (
        <div className="group-menu-backdrop" onClick={() => setOnboardingGroupPrompt({ open: false, personName: null })}>
          <div className="onboarding-group-prompt" onClick={e => e.stopPropagation()}>
            <div className="onboarding-group-prompt-header">
              <h3>Add {onboardingGroupPrompt.personName} to a group</h3>
              <p>Groups help you organize your people</p>
            </div>
            <div className="onboarding-group-prompt-options">
              <input
                id="onboarding-group-name-input"
                type="text"
                className="onboarding-group-input"
                placeholder="Group name (e.g., Investors, Team, Advisors)"
                autoFocus
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    document.getElementById('onboarding-create-group-btn')?.click();
                  }
                }}
              />
              <button
                id="onboarding-create-group-btn"
                className="onboarding-group-create-btn"
                onClick={async () => {
                  const input = document.getElementById('onboarding-group-name-input');
                  const groupName = input?.value?.trim();
                  if (!groupName) return;

                  const colors = ['#a78bfa', '#4ade80', '#fbbf24', '#60a5fa', '#f472b6', '#ef4444'];
                  const randomColor = colors[Math.floor(Math.random() * colors.length)];
                  const localMode = isLocalModeEnabled();
                  const newGroup = localMode
                    ? addLocalGroup(groupName, randomColor, null)
                    : await createGroup(user.id, groupName, randomColor, null);
                  if (newGroup) {
                    const formatted = supabaseGroupToAppFormat(newGroup);
                    setLocalGroups(prev => [...prev, formatted]);
                    // Add person to the new group
                    if (localMode) {
                      addLocalDonorToGroup(onboardingGroupPrompt.personName, formatted.id);
                    } else {
                      await authFetch('/api/donors/add-to-group', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          personName: onboardingGroupPrompt.personName,
                          groupId: formatted.id
                        })
                      });
                    }
                    if (onRefreshDonors) await onRefreshDonors();
                    setOnboardingGroupPrompt({ open: false, personName: null });
                  }
                }}
              >
                Create & Add
              </button>
            </div>
            <button
              className="onboarding-group-skip"
              onClick={() => setOnboardingGroupPrompt({ open: false, personName: null })}
            >
              Skip for now
            </button>
          </div>
        </div>
      )}

      {/* Add to Group Modal */}
      <DesktopAddToGroupModal
        isOpen={addToGroupModal.open}
        onClose={() => setAddToGroupModal({ open: false, group: null })}
        group={addToGroupModal.group}
        allPeople={donors}
        onSelectExisting={handleAddExistingToGroup}
        onAddNew={handleAddNewFromGroup}
      />

      {/* Add Choice Modal (Add Person vs Import) */}
      {addChoiceModalOpen && (
        <div className="group-menu-backdrop" onClick={() => { setAddChoiceModalOpen(false); setAddChoicePosition(null); }}>
          <div
            className="add-choice-modal"
            style={addChoicePosition ? {
              position: 'fixed',
              top: addChoicePosition.top,
              left: addChoicePosition.left
            } : undefined}
            onClick={e => e.stopPropagation()}
          >
            <div className="add-choice-header">
              <h4 className="add-choice-title">Add</h4>
            </div>
            <div className="add-choice-options">
              <button
                className="add-choice-option"
                onClick={() => {
                  setAddChoiceModalOpen(false);
                  setAddPersonPosition(addChoicePosition);
                  setAddPersonModalOpen(true);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M20 8v6M23 11h-6"/>
                </svg>
                Add Person
              </button>
              <button
                className="add-choice-option"
                onClick={() => {
                  setAddChoiceModalOpen(false);
                  setAddChoicePosition(null);
                  setImportModalOpen(true);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      <DesktopImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        groups={localGroups}
        allPeople={donors}
        onImportComplete={(groupInfo) => {
          onRefreshDonors?.(); // This refreshes all data including groups
          // Navigate to the imported group
          if (groupInfo?.id) {
            navigateTo('group', { id: groupInfo.id, name: groupInfo.name });
          }
        }}
        authFetch={authFetch}
      />

      {/* Edit Context Modal */}
      {editContextModal.open && (
        <div className="edit-context-modal-backdrop" onClick={handleCloseEditContext}>
          <div
            className={`edit-context-modal ${editContextModal.position ? 'positioned' : ''}`}
            style={editContextModal.position ? {
              position: 'fixed',
              top: editContextModal.position.top,
              left: editContextModal.position.left
            } : undefined}
            onClick={e => e.stopPropagation()}
          >
            <div className="edit-context-modal-header">
              <svg className="edit-context-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <div className="edit-context-modal-title">
                <h3>Edit Context</h3>
                <span>{editContextModal.group?.name}</span>
              </div>
              <button className="edit-context-modal-close" onClick={handleCloseEditContext}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="edit-context-modal-body">
              <textarea
                ref={editContextInputRef}
                className="edit-context-textarea"
                placeholder="Describe this group... What are they focused on? What's important to know?"
                value={editContextValue}
                onChange={(e) => setEditContextValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleSaveContext();
                  }
                  if (e.key === 'Escape') {
                    handleCloseEditContext();
                  }
                }}
              />
            </div>
            <div className="edit-context-modal-footer">
              <span className="edit-context-hint">
                <kbd>⌘</kbd><kbd>Enter</kbd> to save
              </span>
              <div className="edit-context-actions">
                <button
                  className="edit-context-research"
                  onClick={handleResearchContext}
                  disabled={isResearchingContext}
                >
                  {isResearchingContext ? (
                    <>
                      <span className="research-spinner"></span>
                      Researching...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                      AI Research
                    </>
                  )}
                </button>
                <button className="edit-context-cancel" onClick={handleCloseEditContext}>
                  Cancel
                </button>
                <button className="edit-context-save" onClick={handleSaveContext}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create List/Table Modal */}
      <DesktopCreateModal
        isOpen={createModal.open}
        onClose={() => setCreateModal({ open: false, type: null, group: null })}
        type={createModal.type}
        group={createModal.group}
        onSubmit={handleCreateListOrTable}
      />

      {/* Undo Toast */}
      {undoItems.length > 0 && (
        <div className="undo-toast-container">
          {undoItems.map(item => (
            <div key={item.id} className="undo-toast">
              <span className="undo-toast-label">{item.label}</span>
              <button
                className="undo-toast-btn"
                onClick={() => handleUndo(item.id)}
              >
                Undo
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div className="support-modal-backdrop" onClick={closeHelpModal}>
          <div
            className="support-modal"
            style={helpModalPosition ? {
              position: 'fixed',
              top: helpModalPosition.top,
              left: helpModalPosition.left
            } : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="support-modal-header">
              <svg className="support-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div className="support-modal-title">
                <h3>Need a little help?</h3>
                <span>Send us a message</span>
              </div>
              <button className="support-modal-close" onClick={closeHelpModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="support-modal-body">
              {helpSuccess ? (
                <div className="support-modal-success">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span>Message sent! We'll be in touch.</span>
                </div>
              ) : (
                <form className="support-modal-form" onSubmit={handleHelpSubmit}>
                  <input
                    type="email"
                    className="support-modal-input"
                    placeholder="Your email (optional)"
                    value={helpEmail}
                    onChange={(e) => setHelpEmail(e.target.value)}
                  />
                  <textarea
                    className="support-modal-textarea"
                    placeholder="How can we help?"
                    value={helpMessage}
                    onChange={(e) => setHelpMessage(e.target.value)}
                    rows={4}
                    required
                  />
                  {helpError && <p className="support-modal-error">{helpError}</p>}
                  <button type="submit" className="support-modal-submit" disabled={helpSubmitting}>
                    {helpSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Tour Tooltips - rendered as portals to escape stacking contexts */}
      {sidebarTourStep === 1 && ReactDOM.createPortal(
        <div
          className="sidebar-tour-tooltip-portal"
          style={{ top: tourTooltipPos.top, left: tourTooltipPos.left }}
        >
          <div className="sidebar-tour-content">
            <div className="sidebar-tour-header">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <span>Chat</span>
            </div>
            <p>Ask me anything — research people, find connections, or get suggestions</p>
            <div className="sidebar-tour-actions">
              <div className="sidebar-tour-next" onClick={() => setSidebarTourStep(2)}>
                Next →
              </div>
              <div className="sidebar-tour-dismiss" onClick={() => setSidebarTourStep(0)}>
                Skip
              </div>
            </div>
          </div>
          <div className="sidebar-tour-arrow"></div>
        </div>,
        document.body
      )}

      {sidebarTourStep === 2 && ReactDOM.createPortal(
        <div
          className="sidebar-tour-tooltip-portal"
          style={{ top: tourTooltipPos.top, left: tourTooltipPos.left }}
        >
          <div className="sidebar-tour-content">
            <div className="sidebar-tour-header">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              <span>Groups</span>
            </div>
            <p>Organize your contacts into groups — Investors, Customers, Board Members, etc.</p>
            <div className="sidebar-tour-actions">
              <div className="sidebar-tour-next" onClick={() => setSidebarTourStep(0)}>
                Got it ✓
              </div>
            </div>
          </div>
          <div className="sidebar-tour-arrow"></div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DesktopApp;
